import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api, { STORAGE_URL } from '../api/axios';
import { useCartStore, SelectedOption } from '../store/cartStore';
import { FiShoppingCart, FiSearch, FiMinus, FiPlus, FiX, FiMapPin, FiClock, FiPhone, FiTruck, FiShoppingBag, FiInstagram, FiTwitter, FiFacebook, FiTag, FiCopy, FiCheck, FiChevronDown, FiUser, FiLogOut, FiEdit2, FiTrash2, FiHome, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import './StorefrontPage.css';

const toEnDigits = (s: string) => s.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString()).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776));

interface StorefrontProps {
  slug?: string;
  subdomainWildcard?: string;
  customDomain?: string;
}

export default function StorefrontPage({ slug: propSlug, subdomainWildcard, customDomain }: StorefrontProps) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(customDomain ? null : (propSlug || params.slug || ''));
  const slug = resolvedSlug || '';
  const isSubdomain = !!propSlug || !!customDomain;
  const storeBase = isSubdomain ? '' : `/store/${slug}`;
  const productUrl = (pSlug: string) => `${storeBase}/product/${pSlug}`;

  // Handle payment callback redirect from backend
  const searchParams = new URLSearchParams(location.search);
  const isPaymentCallback = location.pathname.includes('/payment-callback') || searchParams.get('status') === 'success' || searchParams.get('status') === 'failed' || searchParams.get('status') === 'cancelled';

  const [paymentResult, setPaymentResult] = useState<{
    status: string; orderNumber?: string; gateway?: string; error?: string; message?: string;
  } | null>(null);

  // On mount: capture payment params into state and clean the URL
  useEffect(() => {
    if (isPaymentCallback) {
      const s = searchParams.get('status');
      if (s) {
        setPaymentResult({
          status: s,
          orderNumber: searchParams.get('order_number') || undefined,
          gateway: searchParams.get('gateway') || undefined,
          error: searchParams.get('error') || undefined,
          message: searchParams.get('message') || undefined,
        });
        // Clean URL — navigate to store root
        navigate(storeBase || '/', { replace: true });
      }
    }
  }, []);

  const [store, setStore] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [allMaxPrice, setAllMaxPrice] = useState<number>(100);
  const [selectedCategory, setSelectedCategory] = useState<string>(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('category') || '';
  });
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [openNavDropdown, setOpenNavDropdown] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [lang, setLang] = useState<'en' | 'ar'>(() => {
    const s = propSlug || params.slug || '';
    const saved = localStorage.getItem(`cartz_lang_${s}`);
    return saved === 'ar' ? 'ar' : 'en';
  });
  const [loading, setLoading] = useState(true);
  const [activePromos, setActivePromos] = useState<any[]>([]);
  const [deliveryAreas, setDeliveryAreas] = useState<any[]>([]);
  const [selectedArea, setSelectedAreaRaw] = useState<any>(null);
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [areaPickerFromCart, setAreaPickerFromCart] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'denied' | 'granted' | 'detecting'>('idle');

  // Persist selected area in localStorage
  const setSelectedArea = (area: any) => {
    setSelectedAreaRaw(area);
    if (area) {
      localStorage.setItem(`cartz_area_${slug}`, JSON.stringify({ id: area.id, name: area.name, name_en: area.name_en }));
    } else {
      localStorage.removeItem(`cartz_area_${slug}`);
    }
  };
  const [areaDetectionDone, setAreaDetectionDone] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [priceRange, setPriceRange] = useState<[string, string]>(['', '']);
  const [pendingPrice, setPendingPrice] = useState<[string, string]>(['', '']);
  const priceTimer = React.useRef<any>(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [customer, setCustomer] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [customerToken, setCustomerToken] = useState<string | null>(() => localStorage.getItem(`cartz_customer_token_${slug}`));

  // Re-read token when slug resolves (custom domain case)
  useEffect(() => {
    if (slug) {
      const saved = localStorage.getItem(`cartz_customer_token_${slug}`);
      if (saved && saved !== customerToken) setCustomerToken(saved);
    }
  }, [slug]);

  const cart = useCartStore();

  const isRTL = lang === 'ar';

  // Dynamically load Google Font
  useEffect(() => {
    if (store?.font_family) {
      const font = store.font_family.replace(/ /g, '+');
      const linkId = 'sf-google-font';
      let link = document.getElementById(linkId) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      link.href = `https://fonts.googleapis.com/css2?family=${font}:wght@300;400;500;600;700&display=swap`;
    }
  }, [store?.font_family]);

  // Set document direction for RTL support (affects toast/snackbar)
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    return () => { document.documentElement.dir = 'ltr'; };
  }, [isRTL]);

  // Live theme editor support via postMessage
  const [themeOverrides, setThemeOverrides] = useState<any>(null);
  const isThemeEditor = new URLSearchParams(window.location.search).get('theme_editor') === '1';
  useEffect(() => {
    if (!isThemeEditor) return;
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'theme-editor-update') {
        setThemeOverrides({ form: e.data.form, layoutSettings: e.data.layoutSettings });
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isThemeEditor]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!openNavDropdown) return;
    const handleClick = () => setOpenNavDropdown(null);
    // Delay to avoid catching the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [openNavDropdown]);

  // Set CSS vars on root so toast/snackbar inherits store styles
  useEffect(() => {
    if (store) {
      const root = document.documentElement;
      root.style.setProperty('--sf-primary', store.primary_color);
      root.style.setProperty('--sf-font', `'${store.font_family}'`);
      return () => {
        root.style.removeProperty('--sf-primary');
        root.style.removeProperty('--sf-font');
      };
    }
  }, [store?.primary_color, store?.font_family]);

  // Resolve custom domain to store slug
  useEffect(() => {
    if (customDomain && !resolvedSlug) {
      api.get(`/storefront/resolve-domain`, { params: { domain: customDomain } })
        .then(res => {
          setResolvedSlug(res.data.slug);
          const savedLang = localStorage.getItem(`cartz_lang_${res.data.slug}`);
          if (savedLang === 'ar') setLang('ar');
          else if (savedLang === 'en') setLang('en');
        })
        .catch(() => setResolvedSlug(''));
    }
  }, [customDomain]);

  useEffect(() => {
    if (slug) loadStore();
  }, [slug]);

  // Inject Tawk.to live chat script
  useEffect(() => {
    if (!store?.tawk_key) return;
    const id = 'tawk-script';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = `https://embed.tawk.to/${store.tawk_key}`;
    s.charset = 'UTF-8';
    s.setAttribute('crossorigin', '*');
    document.head.appendChild(s);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, [store?.tawk_key]);

  // Load products when filters change; reset price when category/search/tag changes
  useEffect(() => {
    if (!slug || !areaDetectionDone) return;
    setPriceRange(['', '']);
    setPendingPrice(['', '']);
    setProductPage(1);
    loadProducts(1, false, true);
  }, [slug, selectedCategory, searchQuery, selectedArea, selectedTag, areaDetectionDone]);

  // Price filter — only triggers API when user actually sets a price range
  const priceInitRef = React.useRef(true);
  useEffect(() => {
    if (priceInitRef.current) { priceInitRef.current = false; return; }
    if (!slug || !areaDetectionDone) return;
    // Skip if both empty (reset by category change, which already loads)
    if (!priceRange[0] && !priceRange[1]) return;
    setProductPage(1);
    loadProducts(1, false);
  }, [priceRange]);

  // Geo-detect helper: resolve coordinates to a matching delivery area
  const detectAreaFromLocation = (areas: any[]): Promise<any | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      setLocationStatus('detecting');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          setLocationStatus('granted');
          try {
            const { latitude, longitude } = position.coords;
            const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9\u0600-\u06FF]/g, '');
            const matchArea = (candidates: string[]): any => {
              for (const candidate of candidates) {
                const cn = normalize(candidate);
                if (!cn || cn.length < 3) continue;
                for (const area of areas) {
                  const names = [area.name, area.name_en].filter(Boolean).map(normalize);
                  if (names.some((n: string) => n === cn || n.includes(cn) || cn.includes(n))) return area;
                }
              }
              return null;
            };
            const [resEn, resAr] = await Promise.all([
              fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en&zoom=16`),
              fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=ar&zoom=16`),
            ]);
            const [dataEn, dataAr] = await Promise.all([resEn.json(), resAr.json()]);
            const addrEn = dataEn.address || {};
            const addrAr = dataAr.address || {};
            const specific = [addrEn.neighbourhood, addrAr.neighbourhood, addrEn.suburb, addrAr.suburb, addrEn.residential, addrAr.residential].filter(Boolean);
            const medium = [addrEn.city_district, addrAr.city_district, addrEn.town, addrAr.town, addrEn.village, addrAr.village].filter(Boolean);
            const broad = [addrEn.city, addrAr.city, addrEn.county, addrAr.county].filter(Boolean);
            resolve(matchArea(specific) || matchArea(medium) || matchArea(broad));
          } catch { resolve(null); }
        },
        () => { setLocationStatus('denied'); resolve(null); },
        { timeout: 5000, maximumAge: 300000 }
      );
    });
  };

  // Auto-detect area: restore from localStorage first, then try geolocation
  useEffect(() => {
    if (areaDetectionDone) return;
    if (loading || !store) return; // Wait for store to finish loading
    if (deliveryAreas.length === 0) {
      // Store has no delivery areas — skip detection
      setAreaDetectionDone(true);
      return;
    }
    if (selectedArea) {
      setAreaDetectionDone(true);
      return;
    }
    // Try restoring from localStorage
    try {
      const saved = localStorage.getItem(`cartz_area_${slug}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const match = deliveryAreas.find((a: any) => a.id === parsed.id);
        if (match) {
          setSelectedAreaRaw(match);
          setAreaDetectionDone(true);
          return;
        }
      }
    } catch {}
    // Fall back to geolocation
    if (!navigator.geolocation) {
      setAreaDetectionDone(true);
      return;
    }
    detectAreaFromLocation(deliveryAreas).then((match) => {
      if (match) setSelectedArea(match);
      setAreaDetectionDone(true);
    });
  }, [deliveryAreas, store, loading]);

  // Sync category to URL
  useEffect(() => {
    const url = new URL(window.location.href);
    if (selectedCategory) {
      url.searchParams.set('category', selectedCategory);
    } else {
      url.searchParams.delete('category');
    }
    window.history.replaceState({}, '', url.toString());
  }, [selectedCategory]);

  // Check if we're on a product page (computed early so useEffect can reference it before early returns)
  const wildcard = subdomainWildcard ?? params['*'] ?? '';
  const productSlugMatch = wildcard.match(/^product\/(.+)$/);
  const productSlugFromUrl = productSlugMatch ? productSlugMatch[1] : null;

  // Scroll to top when entering product page
  useEffect(() => {
    if (productSlugFromUrl) window.scrollTo(0, 0);
  }, [productSlugFromUrl]);

  const loadStore = async () => {
    try {
      const [storeRes, catRes] = await Promise.all([
        api.get(`/storefront/${slug}`),
        api.get(`/storefront/${slug}/categories`),
      ]);
      setStore(storeRes.data);
      setCategories(catRes.data);
      // Load active promos
      try {
        const promoRes = await api.get(`/storefront/${slug}/promos`);
        setActivePromos(promoRes.data);
      } catch {}
      // Load delivery areas and branches
      try {
        const areaRes = await api.get(`/storefront/${slug}/delivery-areas`);
        setDeliveryAreas(areaRes.data);
      } catch {}
      try {
        const branchRes = await api.get(`/storefront/${slug}/branches`);
        setBranches(branchRes.data || []);
      } catch {}
    } catch {
      setStore(null);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async (page = 1, append = false, skipPrice = false) => {
    if (!append) setProductsLoading(true);
    try {
      const params: any = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      if (selectedArea) params.area_id = selectedArea.id;
      if (selectedTag) params.tag = selectedTag;
      if (!skipPrice) {
        if (priceRange[0]) params.min_price = priceRange[0];
        if (priceRange[1]) params.max_price = priceRange[1];
      }
      // Use pagination for advanced layout (30 per page)
      params.per_page = 30;
      params.page = page;
      const res = await api.get(`/storefront/${slug}/products`, { params });
      // Handle both paginated { data, total, has_more } and plain array responses
      const isPagedResponse = res.data && !Array.isArray(res.data) && Array.isArray(res.data.data);
      const items = isPagedResponse ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      if (append) {
        setProducts(prev => [...prev, ...items]);
      } else {
        setProducts(items);
      }
      // Use max_price from backend (covers all products, not just current page)
      if (isPagedResponse && res.data.max_price) {
        setAllMaxPrice(Math.ceil(Number(res.data.max_price)));
      }
      setTotalProducts(isPagedResponse ? res.data.total : items.length);
      setHasMoreProducts(isPagedResponse ? res.data.has_more : false);
      setProductPage(page);
    } catch {} finally {
      setProductsLoading(false);
    }
  };

  const loadMoreProducts = async () => {
    setLoadingMore(true);
    await loadProducts(productPage + 1, true);
    setLoadingMore(false);
  };

  // Load customer profile if token exists
  useEffect(() => {
    if (customerToken && slug) {
      api.get(`/storefront/${slug}/customer/profile`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      }).then(res => setCustomer(res.data))
        .catch(() => {
          setCustomer(null);
          setCustomerToken(null);
          localStorage.removeItem(`cartz_customer_token_${slug}`);
        });
    }
  }, [customerToken, slug]);

  const customerLogin = async (email: string, password: string) => {
    const res = await api.post(`/storefront/${slug}/customers/login`, { email, password });
    const { customer: c, token } = res.data;
    setCustomer(c);
    setCustomerToken(token);
    localStorage.setItem(`cartz_customer_token_${slug}`, token);
    return c;
  };

  const customerRegister = async (name: string, email: string, phone: string, password: string, password_confirmation: string) => {
    const res = await api.post(`/storefront/${slug}/customers/register`, { name, email, phone, password, password_confirmation });
    const { customer: c, token } = res.data;
    setCustomer(c);
    setCustomerToken(token);
    localStorage.setItem(`cartz_customer_token_${slug}`, token);
    return c;
  };

  const customerLogout = () => {
    if (customerToken) {
      api.post(`/storefront/${slug}/customer/logout`, {}, {
        headers: { Authorization: `Bearer ${customerToken}` },
      }).catch(() => {});
    }
    setCustomer(null);
    setCustomerToken(null);
    localStorage.removeItem(`cartz_customer_token_${slug}`);
  };

  const [modalOptions, setModalOptions] = useState<Record<string, Record<string, number>>>({});
  const [modalNotes, setModalNotes] = useState('');

  const resetModalOptions = () => { setModalOptions({}); setModalNotes(''); };

  const getSelectedOptionsForCart = (product: any): SelectedOption[] => {
    const result: SelectedOption[] = [];
    if (!product.option_groups?.length) return result;
    for (const group of product.option_groups) {
      const selections = modalOptions[group.name] || {};
      for (const [choiceName, qty] of Object.entries(selections)) {
        if (qty > 0) {
          const choice = group.choices?.find((c: any) => (c.name || c.name_ar) === choiceName);
          result.push({
            groupName: lang === 'ar' && group.name_ar ? group.name_ar : group.name,
            choiceName: lang === 'ar' && choice?.name_ar ? choice.name_ar : choiceName,
            price: parseFloat(choice?.price || '0'),
            quantity: qty,
          });
        }
      }
    }
    return result;
  };

  const getModalOptionsTotal = (product: any): number => {
    if (!product?.option_groups?.length) return 0;
    let total = 0;
    for (const group of product.option_groups) {
      const selections = modalOptions[group.name] || {};
      for (const [choiceName, qty] of Object.entries(selections)) {
        if (qty > 0) {
          const choice = group.choices?.find((c: any) => (c.name || c.name_ar) === choiceName);
          total += parseFloat(choice?.price || '0') * qty;
        }
      }
    }
    return total;
  };

  const validateOptions = (product: any): string | null => {
    if (!product.option_groups?.length) return null;
    for (const group of product.option_groups) {
      if (!group.required) continue;
      const selections = modalOptions[group.name] || {};
      const totalSelected = Object.values(selections).reduce((s: number, q: number) => s + (q > 0 ? 1 : 0), 0);
      const min = group.min_selections || 1;
      if (totalSelected < min) {
        const gName = lang === 'ar' && group.name_ar ? group.name_ar : group.name;
        return lang === 'ar' ? `يرجى اختيار ${gName}` : `Please select ${gName}`;
      }
    }
    return null;
  };

  const toggleRadio = (groupName: string, choiceName: string) => {
    setModalOptions(prev => ({ ...prev, [groupName]: { [choiceName]: 1 } }));
  };

  const toggleCheckbox = (groupName: string, choiceName: string, maxSelections: number) => {
    setModalOptions(prev => {
      const group = { ...(prev[groupName] || {}) };
      if (group[choiceName]) {
        delete group[choiceName];
      } else {
        const currentCount = Object.values(group).filter(v => v > 0).length;
        if (maxSelections && currentCount >= maxSelections) return prev;
        group[choiceName] = 1;
      }
      return { ...prev, [groupName]: group };
    });
  };

  const setOptionQty = (groupName: string, choiceName: string, qty: number, maxQty: number) => {
    setModalOptions(prev => {
      const group = { ...(prev[groupName] || {}) };
      if (qty <= 0) delete group[choiceName];
      else group[choiceName] = Math.min(qty, maxQty || 99);
      return { ...prev, [groupName]: group };
    });
  };

  const addToCart = (product: any, skipOptions?: boolean) => {
    if (!skipOptions && product.option_groups?.length) {
      const err = validateOptions(product);
      if (err) { toast.error(err); return; }
    }
    const selectedOpts = getSelectedOptionsForCart(product);
    cart.addItem({
      product_id: product.id,
      name: lang === 'ar' && product.name_ar ? product.name_ar : product.name,
      price: parseFloat(product.price),
      image: product.image,
      selectedOptions: selectedOpts.length ? selectedOpts : undefined,
      notes: modalNotes || undefined,
    });
    toast.success(lang === 'ar' ? 'تمت الإضافة للسلة' : 'Added to cart');
    resetModalOptions();
  };

  const addToCartQuick = (product: any) => {
    if (product.option_groups?.length) {
      setSelectedProduct(product);
      resetModalOptions();
      return;
    }
    addToCart(product, true);
  };

  // Clear cart on successful payment
  useEffect(() => {
    if (paymentResult?.status === 'success') cart.clearCart();
  }, [paymentResult]);

  // Infinite scroll
  const infiniteScrollEnabled = store?.layout_settings?.infinite_scroll || false;
  const loadingMoreRef = useRef(false);
  const infiniteScrollCb = useCallback(() => {
    if (!infiniteScrollEnabled || !hasMoreProducts || loadingMoreRef.current) return;
    const scrollBottom = window.innerHeight + window.scrollY;
    if (scrollBottom >= document.body.offsetHeight - 400) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      loadProducts(productPage + 1, true).then(() => {
        setLoadingMore(false);
        loadingMoreRef.current = false;
      });
    }
  }, [infiniteScrollEnabled, hasMoreProducts, productPage]);

  useEffect(() => {
    if (!infiniteScrollEnabled) return;
    window.addEventListener('scroll', infiniteScrollCb);
    return () => window.removeEventListener('scroll', infiniteScrollCb);
  }, [infiniteScrollEnabled, infiniteScrollCb]);

  if (loading) {
    return (
      <div className="sf-loading">
        <div className="sf-spinner" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="sf-not-found">
        <h1>Store not found</h1>
        <p>The store you're looking for doesn't exist or is inactive.</p>
      </div>
    );
  }

  // Apply live theme editor overrides
  const sto = themeOverrides ? {
    ...store,
    ...themeOverrides.form,
    layout_settings: { ...store.layout_settings, ...themeOverrides.layoutSettings },
  } : store;

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const layout = sto?.layout || 'modern';
  const storeBrands = sto?.layout_settings?.brands || [];
  const storeName = (lang === 'ar' && sto?.name_ar) ? sto.name_ar : sto?.name;
  const logoStyle: React.CSSProperties = {
    width: sto?.layout_settings?.logo_width ? `${sto.layout_settings.logo_width}px` : undefined,
    height: sto?.layout_settings?.logo_height ? `${sto.layout_settings.logo_height}px` : undefined,
    objectFit: 'contain' as const,
  };
  const stickyNav = sto?.layout_settings?.sticky_nav || false;
  const stickyFilters = sto?.layout_settings?.sticky_filters || false;
  const infiniteScroll = infiniteScrollEnabled;
  const fontSizes: React.CSSProperties & Record<string, string> = {
    '--sf-font-title': `${sto?.layout_settings?.font_size_title || 24}px`,
    '--sf-font-menu': `${sto?.layout_settings?.font_size_menu || 14}px`,
    '--sf-font-product-name': `${sto?.layout_settings?.font_size_product_name || 16}px`,
    '--sf-font-product-price': `${sto?.layout_settings?.font_size_product_price || 16}px`,
    '--sf-font-body': `${sto?.layout_settings?.font_size_body || 14}px`,
  } as any;

  const handleRetryPayment = async () => {
    if (!paymentResult?.orderNumber || !paymentResult?.gateway) return;
    try {
      const payRes = await api.post(`/storefront/${slug}/orders/${encodeURIComponent(paymentResult.orderNumber)}/pay`, {
        gateway: paymentResult.gateway,
        lang,
      });
      if (payRes.data.redirect_url) {
        window.location.href = payRes.data.redirect_url;
      } else if (payRes.data.payment_url && payRes.data.form_data) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = payRes.data.payment_url;
        Object.entries(payRes.data.form_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value as string;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Failed to retry payment', '\u0641\u0634\u0644 \u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062F\u0641\u0639'));
    }
  };

  const renderBrands = () => {
    if (!storeBrands.length) return null;
    return (
      <div className="sf-brands">
        <div className="sf-brands-inner">
          {storeBrands.map((brand: any, i: number) => {
            const img = (
              <img
                key={i}
                src={brand.logo?.startsWith('http') ? brand.logo : `/storage/${brand.logo}`}
                alt={brand.name}
                title={brand.name}
                className="sf-brand-logo"
              />
            );
            return brand.url ? (
              <a key={i} href={brand.url} target="_blank" rel="noopener noreferrer">{img}</a>
            ) : (
              img
            );
          })}
        </div>
      </div>
    );
  };

  // Pagination / load-more control — shared across all layouts
  const renderLoadMore = () => {
    if (!hasMoreProducts) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
        {infiniteScroll ? (
          loadingMore && <span style={{ color: 'var(--gray-400)', fontSize: 14 }}>{t('Loading...', 'جاري التحميل...')}</span>
        ) : (
          <button
            onClick={loadMoreProducts}
            disabled={loadingMore}
            style={{
              padding: '12px 40px', borderRadius: 10, border: `2px solid ${sto.primary_color || '#3b82f6'}`,
              background: 'transparent', color: sto.primary_color || '#3b82f6',
              fontSize: 15, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            {loadingMore
              ? t('Loading...', 'جاري التحميل...')
              : t('Show More', 'عرض المزيد')}
          </button>
        )}
      </div>
    );
  };


  // Product detail page flag (rendered inside the layout with header/footer)
  const showProductPage = !!productSlugFromUrl;

  return (
    <div
      className={`storefront sf-${layout} ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        '--sf-primary': sto.primary_color,
        '--sf-secondary': sto.secondary_color,
        '--sf-accent': sto.accent_color,
        '--sf-text': sto.text_color,
        '--sf-bg': sto.background_color,
        '--sf-font': sto.font_family,
        fontFamily: `'${sto.font_family}', sans-serif`,
        background: sto.background_color,
        color: sto.text_color,
        ...fontSizes,
      } as React.CSSProperties}
    >
      <Helmet>
        <html lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} />
        <title>{(lang === 'ar' ? (store.meta_title_ar || store.name_ar) : store.meta_title) || store.name}</title>
        <meta name="description" content={(lang === 'ar' ? (store.meta_description_ar || store.description_ar) : store.meta_description) || store.description} />
        <link rel="canonical" href={window.location.origin + '/'} />
        <link rel="alternate" hrefLang="ar" href={`${window.location.origin}/?lang=ar`} />
        <link rel="alternate" hrefLang="en" href={`${window.location.origin}/?lang=en`} />
        <link rel="alternate" hrefLang="x-default" href={`${window.location.origin}/`} />
        {(store.favicon || store.logo) && <>
          <link rel="icon" href={`${STORAGE_URL}/${store.favicon || store.logo}`} />
          <link rel="apple-touch-icon" href={`${STORAGE_URL}/${store.favicon || store.logo}`} />
        </>}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={(lang === 'ar' ? (store.meta_title_ar || store.name_ar) : store.meta_title) || store.name} />
        <meta property="og:url" content={window.location.origin + '/'} />
        {store.logo && <meta property="og:image" content={`${STORAGE_URL}/${store.logo}`} />}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: store.name,
          url: window.location.origin,
          logo: store.logo ? `${STORAGE_URL}/${store.logo}` : undefined,
          description: store.description || undefined,
          contactPoint: store.phone ? { '@type': 'ContactPoint', telephone: store.phone, contactType: 'customer service' } : undefined,
          sameAs: [store.instagram, store.facebook, store.twitter, store.tiktok].filter(Boolean),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: store.name,
          url: window.location.origin,
        })}</script>
      </Helmet>

      {/* Announcement Bar */}
      {(store.announcement || store.announcement_ar) && (
        <div className="sf-announcement" style={{ background: sto.primary_color }}>
          {lang === 'ar' ? (store.announcement_ar || store.announcement) : store.announcement}
        </div>
      )}

      {/* Promo Banner */}
      {activePromos.length > 0 && (
        <div className="sf-promo-banner">
          {activePromos.map((promo: any, i: number) => (
            <div key={i} className="sf-promo-chip" style={{ borderColor: sto.primary_color }}>
              <FiTag style={{ color: sto.primary_color }} />
              <span className="sf-promo-text">
                {promo.discount_type === 'percentage'
                  ? `${promo.discount_value}% ${t('OFF', 'خصم')}`
                  : `${store.currency_symbol} ${Number(promo.discount_value).toFixed(3)} ${t('OFF', 'خصم')}`}
                {promo.min_order_amount > 0 && ` ${t('on orders over', 'للطلبات فوق')} ${store.currency_symbol} ${Number(promo.min_order_amount).toFixed(3)}`}
              </span>
              <button
                className="sf-promo-copy"
                style={{ background: sto.primary_color }}
                onClick={() => {
                  navigator.clipboard.writeText(promo.code);
                  toast.success(t('Code copied!', 'تم نسخ الكود!'));
                }}
              >
                <FiCopy /> {promo.code}
              </button>
            </div>
          ))}
        </div>
      )}

      {layout === 'food' ? (
        /* ============ FOOD LAYOUT: Split View ============ */
        <>
          <div className="sf-food-split">
            {/* Left: Banner/Image only */}
            <div className="sf-food-left">
              {/* Action buttons over image */}
              <div className="sf-food-actions">
                <button className="sf-lang-toggle" onClick={() => { const next = lang === 'en' ? 'ar' : 'en'; setLang(next); localStorage.setItem(`cartz_lang_${slug}`, next); }}>
                  {lang === 'en' ? 'عربي' : 'EN'}
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => customer ? setShowAuthModal(true) : setShowAuthModal(true)} title={customer ? customer.name : t('Account', 'حسابي')}>
                  <FiUser />
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => setShowCart(true)}>
                  <FiShoppingBag />
                  {cart.getItemCount() > 0 && (
                    <span className="sf-cart-badge">{cart.getItemCount()}</span>
                  )}
                </button>
                <button className="sf-search-toggle" onClick={() => {
                  const el = document.querySelector('.sf-food-search-input') as HTMLInputElement;
                  if (el) el.focus();
                }}>
                  <FiSearch />
                </button>
              </div>
              {store.cover_image ? (
                <img src={`${STORAGE_URL}/${store.cover_image}`} alt={store.name} className="sf-food-banner" />
              ) : (
                <div className="sf-food-banner-placeholder" style={{ background: sto.primary_color }}>
                  {store.logo && <img src={`${STORAGE_URL}/${store.logo}`} alt={store.name} className="sf-food-banner-logo" style={logoStyle} />}
                  <h2>{storeName}</h2>
                </div>
              )}
              {/* Social floating buttons */}
              <div className="sf-food-social-float">
                {store.whatsapp && (
                  <a href={`https://wa.me/${store.whatsapp}`} target="_blank" rel="noopener noreferrer" className="sf-food-social-btn sf-social-whatsapp">
                    <FiPhone />
                  </a>
                )}
                {store.instagram && (
                  <a href={`https://instagram.com/${store.instagram}`} target="_blank" rel="noopener noreferrer" className="sf-food-social-btn sf-social-instagram">
                    <FiInstagram />
                  </a>
                )}
              </div>
            </div>

            {/* Right: Store info + Menu */}
            <div className="sf-food-right">
              {/* Store header with logo + name */}
              <div className="sf-food-store-top">
                {store.logo && <img src={`${STORAGE_URL}/${store.logo}`} alt="" className="sf-food-store-logo" style={logoStyle} />}
                <div>
                  <h2 className="sf-food-store-name">{storeName}</h2>
                  {store.description && <p className="sf-food-store-desc">{store.description}</p>}
                </div>
              </div>

              {showProductPage ? (
                <ProductPage
                  slug={slug}
                  productSlug={productSlugFromUrl}
                  store={store}
                  lang={lang}
                  isRTL={isRTL}
                  layout={layout}
                  cart={cart}
                  t={t}
                  storeBase={storeBase}
                />
              ) : (
              <>
              {/* Delivery / Pickup Toggle - hide if only one option */}
              {store.delivery_enabled && store.pickup_enabled && branches.length > 0 && (
              <div className="sf-food-type-section">
                <div className="sf-food-type-toggle">
                  <button className="sf-food-type-btn active" style={{ borderColor: sto.primary_color }}>
                    {t('Delivery', 'توصيل')}
                  </button>
                  <button className="sf-food-type-btn">
                    {t('Pickup', 'استلام')}
                  </button>
                </div>
              </div>
              )}

              {/* Delivery Info rows */}
              <div className="sf-food-store-info">
                <div className="sf-food-delivery-info">
                  <div className="sf-food-info-row" onClick={() => setShowAreaPicker(true)} style={{ cursor: 'pointer' }}>
                    <span><FiMapPin /> {t('Deliver to', 'توصيل الى')}</span>
                    <span className="sf-food-info-action">
                      {selectedArea
                        ? (lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name))
                        : t('Select area', 'اختر منطقة')}
                    </span>
                  </div>
                  {selectedArea && (
                    <div className="sf-food-info-row">
                      <span><FiTruck /> {t('Delivery Fee', 'رسوم التوصيل')}</span>
                      <span>{store.currency_symbol} {Number(selectedArea.delivery_fee || 0).toFixed(3)}</span>
                    </div>
                  )}
                  {(selectedArea?.estimated_time || store.estimated_delivery_time) ? (
                    <div className="sf-food-info-row">
                      <span><FiClock /> {t('Fastest arrival', 'أقرب وصول')}</span>
                      <span>{selectedArea?.estimated_time || store.estimated_delivery_time} {selectedArea?.estimated_time_unit === 'days' ? (selectedArea?.business_days ? t('business days', 'يوم عمل') : t('days', 'يوم')) : selectedArea?.estimated_time_unit === 'hours' ? t('hours', 'ساعة') : t('min', 'دقيقة')}</span>
                    </div>
                  ) : null}
                </div>
                {!store.is_open && (
                  <div className="sf-food-closed" style={{ background: sto.primary_color }}>
                    {t('Currently Closed', 'مغلق حالياً')}
                  </div>
                )}
              </div>

              {/* Search */}
              <div className="sf-food-search">
                <FiSearch />
                <input
                  type="text"
                  className="sf-food-search-input"
                  placeholder={t('Search products...', 'البحث عن المنتجات...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Categories as accordion sections with products */}
              <div className="sf-food-menu">
                {categories.map((cat) => {
                  const catProducts = products.filter((p: any) => p.category_id === cat.id);
                  const isCollapsed = collapsedCategories.has(cat.slug);
                  if (catProducts.length === 0) return null;
                  return (
                    <div key={cat.id} className="sf-food-menu-section">
                      <div className="sf-food-menu-header" onClick={() => {
                        setCollapsedCategories(prev => {
                          const next = new Set(prev);
                          if (next.has(cat.slug)) next.delete(cat.slug);
                          else next.add(cat.slug);
                          return next;
                        });
                      }}>
                        {cat.image && <img src={`${STORAGE_URL}/${cat.image}`} alt="" className="sf-food-cat-img" />}
                        <h3>{lang === 'ar' && cat.name_ar ? cat.name_ar : cat.name}</h3>
                        <span className="sf-food-menu-toggle">{isCollapsed ? '▼' : '▲'}</span>
                      </div>
                      {!isCollapsed && catProducts.map((product: any) => (
                        <div key={product.id} className="sf-food-item" onClick={() => setSelectedProduct(product)}>
                          {product.image && (
                            <img src={`${STORAGE_URL}/${product.image}`} alt="" className="sf-food-item-img" onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                          )}
                          <div className="sf-food-item-info">
                            <h4>{lang === 'ar' && product.name_ar ? product.name_ar : product.name}</h4>
                            {product.description && (
                              <p className="sf-food-item-desc">
                                {lang === 'ar' && product.description_ar ? product.description_ar : product.description}
                              </p>
                            )}
                            <div className="sf-food-item-bottom">
                              <span className="sf-food-item-price" style={{ color: sto.primary_color }}>
                                {store.currency_symbol} {Number(product.price).toFixed(3)}
                              </span>
                              <button
                                className="sf-food-add-btn"
                                onClick={(e) => { e.stopPropagation(); addToCartQuick(product); }}
                                disabled={!store.is_open}
                                style={{ color: sto.primary_color, borderColor: sto.primary_color }}
                              >
                                + {t('Add', 'أضف')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
                {products.length === 0 && (
                  <div className="sf-empty">
                    <FiShoppingBag size={48} />
                    <p>{t('No products found', 'لا توجد منتجات')}</p>
                  </div>
                )}
              </div>
              {renderLoadMore()}

              </>
              )}
              {/* Brands & Footer inside right panel */}
              {renderBrands()}
              <footer className="sf-footer sf-food-footer">
                <div className="sf-footer-inner">
                  <div className="sf-footer-powered">
                    <a href="https://cartz.cc" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{t('Powered by', 'مدعوم بواسطة')} <strong>Cartz</strong></a>
                  </div>
                </div>
              </footer>
            </div>
          </div>
        </>
      ) : layout === 'advanced' ? (
        /* ============ ADVANCED E-COMMERCE LAYOUT ============ */
        <>
          <header className={`sf-header sf-header-advanced${stickyNav ? ' sf-header-sticky' : ''}`}>
            <div className="sf-header-inner">
              <div className="sf-header-left" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }} onClick={() => navigate(storeBase || '/')}>
                {store.logo && (
                  <img src={`${STORAGE_URL}/${store.logo}`} alt={store.name} className="sf-logo sf-logo-lg" style={logoStyle} />
                )}
                <h1 className="sf-store-name">{storeName}</h1>
              </div>
              <div className="sf-adv-search-bar">
                <FiSearch />
                <input
                  type="text"
                  placeholder={t('Search products...', 'البحث عن المنتجات...')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); if (showProductPage) navigate(storeBase || '/'); }}
                />
              </div>
              <div className="sf-header-right">
                <button className="sf-lang-toggle" onClick={() => { const next = lang === 'en' ? 'ar' : 'en'; setLang(next); localStorage.setItem(`cartz_lang_${slug}`, next); }}>
                  {lang === 'en' ? 'عربي' : 'EN'}
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => customer ? setShowAuthModal(true) : setShowAuthModal(true)} title={customer ? customer.name : t('Account', 'حسابي')}>
                  <FiUser />
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => setShowCart(true)}>
                  <FiShoppingBag />
                  {cart.getItemCount() > 0 && (
                    <span className="sf-cart-badge">{cart.getItemCount()}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Search Bar (shown when search icon tapped) */}
            {showMobileSearch && (
              <div className="sf-mobile-search-bar">
                <FiSearch />
                <input
                  type="text"
                  autoFocus
                  placeholder={t('Search products...', 'البحث عن المنتجات...')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); if (showProductPage) navigate(storeBase || '/'); }}
                />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                    <FiX />
                  </button>
                )}
              </div>
            )}

            {/* Categories nav */}
            <nav className={`sf-adv-nav${stickyNav ? ' sf-adv-nav-sticky' : ''}`}>
              <button className={`sf-adv-nav-btn ${!selectedCategory ? 'active' : ''}`} onClick={() => { setSelectedCategory(''); setOpenNavDropdown(null); if (showProductPage) navigate(storeBase || '/'); }}>
                {t('All', 'الكل')}
              </button>
              {categories.map((cat) => {
                const hasSubs = cat.children?.length > 0;
                const isOpen = openNavDropdown === cat.slug;
                return (
                <div key={cat.id} className={`sf-adv-nav-item ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                  <button
                    className={`sf-adv-nav-btn ${selectedCategory === cat.slug || cat.children?.some((c: any) => selectedCategory === c.slug) ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasSubs) {
                        setOpenNavDropdown(isOpen ? null : cat.slug);
                      } else {
                        setSelectedCategory(cat.slug);
                        setOpenNavDropdown(null);
                        if (showProductPage) navigate(storeBase || '/');
                      }
                    }}
                  >
                    <span>{lang === 'ar' && cat.name_ar ? cat.name_ar : cat.name}</span>
                    {hasSubs && <span className={`sf-adv-nav-arrow ${isOpen ? 'open' : ''}`}>▾</span>}
                  </button>
                  {hasSubs && isOpen && (
                    <div className="sf-adv-nav-dropdown" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`sf-adv-nav-drop-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                        onClick={() => { setSelectedCategory(cat.slug); setOpenNavDropdown(null); if (showProductPage) navigate(storeBase || '/'); }}
                      >
                        {t('All', 'الكل')} {lang === 'ar' && cat.name_ar ? cat.name_ar : cat.name}
                      </button>
                      {cat.children.map((sub: any) => (
                        <button
                          key={sub.id}
                          className={`sf-adv-nav-drop-btn ${selectedCategory === sub.slug ? 'active' : ''}`}
                          onClick={() => { setSelectedCategory(sub.slug); setOpenNavDropdown(null); if (showProductPage) navigate(storeBase || '/'); }}
                        >
                          {lang === 'ar' && sub.name_ar ? sub.name_ar : sub.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
            </nav>
          </header>

          {/* Area selector bar */}
          <div className="sf-adv-info-bar">
            <span className="sf-info-area" onClick={() => setShowAreaPicker(true)} style={{ cursor: 'pointer' }}>
              <FiMapPin /> {selectedArea
                ? (lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name))
                : t('Select area', 'اختر منطقة')}
            </span>
            {selectedArea && (
              <>
                <span><FiTruck /> {store.currency_symbol} {Number(selectedArea.delivery_fee || 0).toFixed(3)}</span>
                {selectedArea.estimated_time && <span><FiClock /> {selectedArea.estimated_time} {selectedArea.estimated_time_unit === 'days' ? (selectedArea.business_days ? t('business days', 'يوم عمل') : t('days', 'يوم')) : selectedArea.estimated_time_unit === 'hours' ? t('hours', 'ساعة') : t('min', 'دقيقة')}</span>}
              </>
            )}
            {!store.is_open && <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{t('Currently Closed', 'مغلق حالياً')}</span>}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginInlineStart: 'auto' }}>
              {store.phone && <a href={`tel:${store.phone}`} style={{ color: 'inherit', fontSize: 13 }}><FiPhone size={14} /> {store.phone}</a>}
              {store.instagram && <a href={`https://instagram.com/${store.instagram}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}><FiInstagram size={16} /></a>}
            </div>
          </div>

          {/* Featured Products */}
          {!showProductPage && products.filter(p => p.is_featured).length > 0 && (
            <div style={{ padding: '20px 24px 0' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>{t('Featured', 'مميز')}</h2>
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
                {products.filter(p => p.is_featured).slice(0, 10).map(product => (
                  <div key={product.id} onClick={() => navigate(productUrl(product.slug))} style={{
                    minWidth: 160, maxWidth: 160, cursor: 'pointer', borderRadius: 12, overflow: 'hidden',
                    border: '1px solid var(--gray-100)', background: 'white', flexShrink: 0,
                  }}>
                    <div style={{ height: 140, background: 'var(--gray-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {product.image ? (
                        <img src={`${STORAGE_URL}/${product.image}`} alt={product.name} onLoad={(e) => e.currentTarget.classList.add('loaded')} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8, opacity: 0, transition: 'opacity 0.4s' }} />
                      ) : <FiPackage size={24} color="var(--gray-300)" />}
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lang === 'ar' && product.name_ar ? product.name_ar : product.name}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: sto.primary_color, marginTop: 4 }}>
                        {store.currency_symbol} {Number(product.effective_price ?? product.price).toFixed(3)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showProductPage ? (
            <ProductPage
              slug={slug}
              productSlug={productSlugFromUrl}
              store={store}
              lang={lang}
              isRTL={isRTL}
              layout={layout}
              cart={cart}
              t={t}
              storeBase={storeBase}
            />
          ) : (
          <div className="sf-adv-layout">
            {/* Sidebar Filters */}
            <aside className={`sf-adv-sidebar${stickyFilters ? ' sf-adv-sidebar-sticky' : ''}`}>
              <h3 className="sf-adv-sidebar-title">{t('Filters', 'التصفية')}</h3>

              {/* Price Range Slider */}
              {(() => {
                const maxPrice = allMaxPrice || 100;
                const minVal = pendingPrice[0] ? Number(pendingPrice[0]) : 0;
                const maxVal = pendingPrice[1] ? Number(pendingPrice[1]) : maxPrice;
                const commitPrice = (newRange: [string, string]) => {
                  if (priceTimer.current) clearTimeout(priceTimer.current);
                  priceTimer.current = setTimeout(() => setPriceRange(newRange), 500);
                };
                return (
                  <div className="sf-adv-filter-group">
                    <h4>{t('Price', 'السعر')}</h4>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                      <span>{store.currency_symbol} {minVal.toFixed(3)}</span>
                      <span>{store.currency_symbol} {maxVal.toFixed(3)}</span>
                    </div>
                    <div className="sf-range-slider">
                      <div className="sf-range-track">
                        <div className="sf-range-fill" style={{
                          left: `${(minVal / maxPrice) * 100}%`,
                          right: `${100 - (maxVal / maxPrice) * 100}%`,
                          background: sto.primary_color,
                        }} />
                      </div>
                      <input
                        type="range" min="0" max={maxPrice} step="0.5"
                        value={minVal}
                        onChange={(e) => {
                          const v = Math.min(Number(e.target.value), maxVal - 0.5);
                          const newRange: [string, string] = [v > 0 ? String(v) : '', pendingPrice[1]];
                          setPendingPrice(newRange);
                          commitPrice(newRange);
                        }}
                        className="sf-range-input"
                      />
                      <input
                        type="range" min="0" max={maxPrice} step="0.5"
                        value={maxVal}
                        onChange={(e) => {
                          const v = Math.max(Number(e.target.value), minVal + 0.5);
                          const newRange: [string, string] = [pendingPrice[0], v < maxPrice ? String(v) : ''];
                          setPendingPrice(newRange);
                          commitPrice(newRange);
                        }}
                        className="sf-range-input"
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Tags */}
              {(() => {
                const allTags = Array.from(new Set(products.flatMap(p => ((lang === 'ar' && p.tags_ar?.length) ? p.tags_ar : (p.tags || [])))));
                return allTags.length > 0 ? (
                  <div className="sf-adv-filter-group">
                    <h4>{t('Tags', 'الوسوم')}</h4>
                    <div className="sf-adv-tags">
                      {selectedTag && (
                        <button className="sf-adv-tag active" onClick={() => setSelectedTag('')}>
                          {t('All', 'الكل')} <FiX size={12} />
                        </button>
                      )}
                      {allTags.map(tag => (
                        <button key={tag} className={`sf-adv-tag ${selectedTag === tag ? 'active' : ''}`} onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}>
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Sort */}
              <div className="sf-adv-filter-group">
                <h4>{t('Sort', 'ترتيب')}</h4>
                <select className="sf-adv-filter-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ width: '100%' }}>
                  <option value="">{t('Default', 'افتراضي')}</option>
                  <option value="price_asc">{t('Price: Low to High', 'السعر: من الأقل')}</option>
                  <option value="price_desc">{t('Price: High to Low', 'السعر: من الأعلى')}</option>
                  <option value="name">{t('Name', 'الاسم')}</option>
                  <option value="newest">{t('Newest', 'الأحدث')}</option>
                </select>
              </div>

              {/* View mode */}
              <div className="sf-adv-filter-group">
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className={`sf-adv-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>▦</button>
                  <button className={`sf-adv-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>☰</button>
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <main className="sf-adv-main">
              <div className="sf-adv-toolbar">
                <span className="sf-adv-count">{products.length} / {totalProducts} {t('products', 'منتج')}</span>
              </div>
              {productsLoading && products.length === 0 ? (
                <div className="sf-adv-grid">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="sf-adv-card" style={{ cursor: 'default' }}>
                      <div style={{ width: '100%', paddingBottom: '100%', position: 'relative' }}>
                        <div className="sf-skeleton" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />
                      </div>
                      <div style={{ padding: 14 }}>
                        <div className="sf-skeleton" style={{ height: 14, width: '80%', marginBottom: 10 }} />
                        <div className="sf-skeleton" style={{ height: 12, width: '50%', marginBottom: 10 }} />
                        <div className="sf-skeleton" style={{ height: 18, width: '35%' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="sf-empty">
                  <FiShoppingBag size={48} />
                  <p>{t('No products found', 'لا توجد منتجات')}</p>
                </div>
              ) : (
                <div className={viewMode === 'list' ? 'sf-adv-list' : 'sf-adv-grid'}>
                  {[...products].sort((a, b) => {
                    if (sortBy === 'price_asc') return a.price - b.price;
                    if (sortBy === 'price_desc') return b.price - a.price;
                    if (sortBy === 'name') return a.name.localeCompare(b.name);
                    if (sortBy === 'newest') return b.id - a.id;
                    return 0;
                  }).map((product) => (
                    <div key={product.id} className={`sf-adv-card ${viewMode}`} onClick={() => navigate(productUrl(product.slug))}>
                      <div className="sf-adv-card-img">
                        {product.image ? (
                          <img src={`${STORAGE_URL}/${product.image}`} alt={product.name} onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                        ) : (
                          <div className="sf-product-placeholder"><FiPackage size={32} /></div>
                        )}
                        {product.compare_price && product.compare_price > product.price && (
                          <span className="sf-discount-badge">
                            {Math.round(((product.compare_price - product.price) / product.compare_price) * 100)}% {t('OFF', 'خصم')}
                          </span>
                        )}
                        {product.is_featured && <span className="sf-featured-badge">{t('Featured', 'مميز')}</span>}
                      </div>
                      <div className="sf-adv-card-body">
                        <h3>{lang === 'ar' && product.name_ar ? product.name_ar : product.name}</h3>
                        {product.description && (
                          <p className="sf-adv-card-desc">
                            {lang === 'ar' && product.description_ar ? product.description_ar : product.description}
                          </p>
                        )}
                        {(() => {
                          const cardTags = (lang === 'ar' && product.tags_ar?.length) ? product.tags_ar : product.tags;
                          return cardTags?.length > 0 ? (
                            <div className="sf-adv-card-tags">
                              {cardTags.slice(0, 3).map((tag: string) => <span key={tag} className="sf-adv-card-tag">{tag}</span>)}
                            </div>
                          ) : null;
                        })()}
                        {product.sku && <span className="sf-adv-card-sku">SKU: {product.sku}</span>}
                        <div className="sf-adv-card-bottom">
                          <div className="sf-product-price">
                            <span className="sf-price" style={{ color: sto.primary_color }}>{store.currency_symbol} {Number(product.price).toFixed(3)}</span>
                            {product.compare_price && (
                              <span className="sf-compare-price">{store.currency_symbol} {Number(product.compare_price).toFixed(3)}</span>
                            )}
                          </div>
                          <button
                            className="sf-adv-add-btn"
                            onClick={(e) => { e.stopPropagation(); addToCartQuick(product); }}
                            disabled={!store.is_open}
                            style={{ background: sto.primary_color }}
                          >
                            {t('+ Add', '+ أضف')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {renderLoadMore()}
            </main>
          </div>
          )}

          {/* WhatsApp floating button */}
          {store.whatsapp && (
            <a href={`https://wa.me/${store.whatsapp}`} target="_blank" rel="noopener noreferrer"
              style={{ position: 'fixed', bottom: 24, insetInlineEnd: 24, width: 56, height: 56, borderRadius: '50%', background: '#25d366', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 99, textDecoration: 'none' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.838-6.313-2.236l-.44-.363-3.095 1.038 1.038-3.095-.363-.44A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            </a>
          )}

          {/* Brands & Footer */}
          {renderBrands()}
          <footer className="sf-footer">
            <div className="sf-footer-inner">
              <div className="sf-footer-info">
                {store.phone && <a href={`tel:${store.phone}`}><FiPhone /> {store.phone}</a>}
                {store.whatsapp && <a href={`https://wa.me/${store.whatsapp}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>}
                {store.address && <span><FiMapPin /> {store.address}</span>}
              </div>
              <div className="sf-footer-social">
                {store.instagram && <a href={`https://instagram.com/${store.instagram}`} target="_blank" rel="noopener noreferrer"><FiInstagram /></a>}
                {store.twitter && <a href={`https://twitter.com/${store.twitter}`} target="_blank" rel="noopener noreferrer"><FiTwitter /></a>}
                {store.facebook && <a href={store.facebook} target="_blank" rel="noopener noreferrer"><FiFacebook /></a>}
              </div>
              <div className="sf-footer-powered">
                <a href="https://cartz.cc" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{t('Powered by', 'مدعوم بواسطة')} <strong>Cartz</strong></a>
              </div>
            </div>
          </footer>
        </>
      ) : (
        /* ============ OTHER LAYOUTS ============ */
        <>
          {/* Header */}
          <header className={`sf-header sf-header-${layout}`}>
            <div className="sf-header-inner">
              <div className="sf-header-left">
                {store.logo ? (
                  <img src={`${STORAGE_URL}/${store.logo}`} alt={store.name} className="sf-logo" style={logoStyle} />
                ) : (
                  <h1 className="sf-store-name">{storeName}</h1>
                )}
              </div>
              <div className="sf-header-right">
                <button className="sf-lang-toggle" onClick={() => { const next = lang === 'en' ? 'ar' : 'en'; setLang(next); localStorage.setItem(`cartz_lang_${slug}`, next); }}>
                  {lang === 'en' ? 'عربي' : 'EN'}
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => customer ? setShowAuthModal(true) : setShowAuthModal(true)} title={customer ? customer.name : t('Account', 'حسابي')}>
                  <FiUser />
                </button>
                <button className="sf-cart-btn sf-hide-mobile" onClick={() => setShowCart(true)}>
                  <FiShoppingBag />
                  {cart.getItemCount() > 0 && (
                    <span className="sf-cart-badge">{cart.getItemCount()}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Cover Image */}
            {store.cover_image && layout !== 'minimal' && (
              <div className="sf-cover" style={{ backgroundImage: `url(${STORAGE_URL}/${store.cover_image})` }}>
                <div className="sf-cover-overlay">
                  <h1 className="sf-cover-title">{storeName}</h1>
                  {store.description && <p className="sf-cover-desc">{store.description}</p>}
                </div>
              </div>
            )}

            {/* Store Info Bar */}
            {layout !== 'minimal' && (
              <div className="sf-info-bar">
                <span className="sf-info-area" onClick={() => setShowAreaPicker(true)} style={{ cursor: 'pointer' }}>
                  <FiMapPin /> {selectedArea
                    ? (lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name))
                    : t('Select area', 'اختر منطقة')}
                </span>
                {selectedArea && (
                  <>
                    <span><FiTruck /> {t('Delivery Fee', 'رسوم التوصيل')}: {store.currency_symbol} {Number(selectedArea.delivery_fee || 0).toFixed(3)}</span>
                    {selectedArea.estimated_time && (
                      <span><FiClock /> {selectedArea.estimated_time} {selectedArea.estimated_time_unit === 'days' ? (selectedArea.business_days ? t('business days', 'يوم عمل') : t('days', 'يوم')) : selectedArea.estimated_time_unit === 'hours' ? t('hours', 'ساعة') : t('min', 'دقيقة')}</span>
                    )}
                  </>
                )}
                {!selectedArea && store.estimated_delivery_time && (
                  <span><FiClock /> {store.estimated_delivery_time} {t('min', 'دقيقة')}</span>
                )}
                {!store.is_open && (
                  <span className="sf-closed">{t('Currently Closed', 'مغلق حالياً')}</span>
                )}
              </div>
            )}
          </header>

          {showProductPage ? (
            <ProductPage
              slug={slug}
              productSlug={productSlugFromUrl}
              store={store}
              lang={lang}
              isRTL={isRTL}
              layout={layout}
              cart={cart}
              t={t}
              storeBase={storeBase}
            />
          ) : (
          <>
          {/* Search & Categories */}
          <div className="sf-filters">
            <div className="sf-search">
              <FiSearch />
              <input
                type="text"
                placeholder={t('Search products...', 'البحث عن المنتجات...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="sf-categories">
              <button
                className={`sf-cat-btn ${!selectedCategory ? 'active' : ''}`}
                onClick={() => setSelectedCategory('')}
              >
                {t('All', 'الكل')}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`sf-cat-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.slug)}
                >
                  {lang === 'ar' && cat.name_ar ? cat.name_ar : cat.name}
                  {cat.products_count > 0 && <span className="sf-cat-count">{cat.products_count}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Products */}
          <main className="sf-products">
            {productsLoading && products.length === 0 ? (
              <div className={`sf-products-grid sf-grid-${layout}`}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="sf-product-card" style={{ cursor: 'default' }}>
                    <div style={{ width: '100%', paddingBottom: '75%', position: 'relative' }}>
                      <div className="sf-skeleton" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />
                    </div>
                    <div style={{ padding: 14 }}>
                      <div className="sf-skeleton" style={{ height: 14, width: '80%', marginBottom: 10 }} />
                      <div className="sf-skeleton" style={{ height: 12, width: '50%', marginBottom: 10 }} />
                      <div className="sf-skeleton" style={{ height: 18, width: '35%' }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="sf-empty">
                <FiShoppingBag size={48} />
                <p>{t('No products found', 'لا توجد منتجات')}</p>
              </div>
            ) : (
              <div className={`sf-products-grid sf-grid-${layout}`}>
                {products.map((product) => (
                  <div key={product.id} className="sf-product-card" onClick={() => navigate(productUrl(product.slug))}>
                    <div className="sf-product-image">
                      {product.image ? (
                        <img src={`${STORAGE_URL}/${product.image}`} alt={product.name} onLoad={(e) => e.currentTarget.classList.add('loaded')} />
                      ) : (
                        <div className="sf-product-placeholder"><FiPackage size={32} /></div>
                      )}
                      {product.compare_price && product.compare_price > product.price && (
                        <span className="sf-discount-badge">
                          {Math.round(((product.compare_price - product.price) / product.compare_price) * 100)}% {t('OFF', 'خصم')}
                        </span>
                      )}
                      {product.is_featured && <span className="sf-featured-badge">{t('Featured', 'مميز')}</span>}
                    </div>
                    <div className="sf-product-info">
                      <h3>{lang === 'ar' && product.name_ar ? product.name_ar : product.name}</h3>
                      {product.description && (
                        <p className="sf-product-desc">
                          {lang === 'ar' && product.description_ar ? product.description_ar : product.description}
                        </p>
                      )}
                      <div className="sf-product-bottom">
                        <div className="sf-product-price">
                          <span className="sf-price">{store.currency_symbol} {Number(product.discount_active ? product.effective_price : product.price).toFixed(3)}</span>
                          {(product.discount_active || (product.compare_price && product.compare_price > product.price)) && (
                            <span className="sf-compare-price">{store.currency_symbol} {Number(product.discount_active ? product.price : product.compare_price).toFixed(3)}</span>
                          )}
                        </div>
                        <button
                          className="sf-add-btn"
                          onClick={(e) => { e.stopPropagation(); addToCartQuick(product); }}
                          disabled={!store.is_open}
                        >
                          <FiPlus />
                        </button>
                      </div>
                      {product.calories && (
                        <span className="sf-calories">{product.calories} {t('cal', 'سعرة')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {renderLoadMore()}
          </main>
          </>
          )}

          {/* Brands & Footer */}
          {renderBrands()}
          <footer className="sf-footer">
            <div className="sf-footer-inner">
              <div className="sf-footer-info">
                {store.phone && <a href={`tel:${store.phone}`}><FiPhone /> {store.phone}</a>}
                {store.address && <span><FiMapPin /> {store.address}</span>}
              </div>
              <div className="sf-footer-social">
                {store.instagram && <a href={`https://instagram.com/${store.instagram}`} target="_blank" rel="noopener noreferrer"><FiInstagram /></a>}
                {store.twitter && <a href={`https://twitter.com/${store.twitter}`} target="_blank" rel="noopener noreferrer"><FiTwitter /></a>}
                {store.facebook && <a href={store.facebook} target="_blank" rel="noopener noreferrer"><FiFacebook /></a>}
              </div>
              <div className="sf-footer-powered">
                <a href="https://cartz.cc" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{t('Powered by', 'مدعوم بواسطة')} <strong>Cartz</strong></a>
              </div>
            </div>
          </footer>
        </>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="sf-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="sf-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sf-modal-close" onClick={() => setSelectedProduct(null)}><FiX /></button>
            {selectedProduct.image && (
              <img src={`${STORAGE_URL}/${selectedProduct.image}`} alt="" className="sf-modal-image" />
            )}
            {selectedProduct.additional_images?.length > 0 && (
              <div className="sf-modal-thumbs">
                {selectedProduct.additional_images.map((img: string, i: number) => (
                  <img key={i} src={`${STORAGE_URL}/${img}`} alt="" className="sf-modal-thumb" />
                ))}
              </div>
            )}
            <div className="sf-modal-body">
              <h2>{lang === 'ar' && selectedProduct.name_ar ? selectedProduct.name_ar : selectedProduct.name}</h2>
              {selectedProduct.description && (
                <p className="sf-modal-desc">
                  {lang === 'ar' && selectedProduct.description_ar ? selectedProduct.description_ar : selectedProduct.description}
                </p>
              )}
              <div className="sf-modal-meta">
                {selectedProduct.calories && <span>{selectedProduct.calories} {t('calories', 'سعرة حرارية')}</span>}
                {selectedProduct.preparation_time && <span><FiClock /> {selectedProduct.preparation_time}</span>}
                {selectedProduct.sku && <span>SKU: {selectedProduct.sku}</span>}
                {selectedProduct.model_number && <span>{t('Model', 'الموديل')}: {selectedProduct.model_number}</span>}
              </div>
              {selectedProduct.tags?.length > 0 && (
                <div className="sf-modal-tags">
                  {selectedProduct.tags.map((tag: string) => (
                    <span key={tag} className="sf-modal-tag"><FiTag size={11} /> {tag}</span>
                  ))}
                </div>
              )}
              {selectedProduct.attributes?.length > 0 && (
                <div className="sf-modal-attrs">
                  {selectedProduct.attributes.map((attr: any, i: number) => (
                    <div key={i} className="sf-modal-attr">
                      <span className="sf-modal-attr-key">{attr.key}</span>
                      <span className="sf-modal-attr-val">{attr.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Option Groups */}
              {selectedProduct.option_groups?.length > 0 && (
                <div className="sf-opt-groups">
                  {selectedProduct.option_groups.map((group: any, gi: number) => {
                    const gName = lang === 'ar' && group.name_ar ? group.name_ar : group.name;
                    const selections = modalOptions[group.name] || {};
                    const selectedCount = Object.values(selections).filter((v: any) => v > 0).length;
                    return (
                      <div key={gi} className="sf-opt-group">
                        <div className="sf-opt-group-header">
                          <h4>{gName}</h4>
                          <div className="sf-opt-group-badges">
                            {group.required ? (
                              <span className="sf-opt-badge sf-opt-required">{t('Required', 'مطلوب')}</span>
                            ) : (
                              <span className="sf-opt-badge sf-opt-optional">{t('Optional', 'إختياري')}</span>
                            )}
                            {(group.min_selections || group.max_selections) && (
                              <span className="sf-opt-limit">
                                {group.min_selections && group.max_selections
                                  ? `${t('Min', 'أدنى')}: ${group.min_selections}, ${t('Max', 'أقصى')}: ${group.max_selections}`
                                  : group.max_selections ? `${t('Max', 'أقصى')}: ${group.max_selections}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="sf-opt-choices">
                          {(group.choices || []).map((choice: any, ci: number) => {
                            const cName = choice.name || choice.name_ar || '';
                            const cLabel = lang === 'ar' && choice.name_ar ? choice.name_ar : choice.name;
                            const cPrice = parseFloat(choice.price || '0');
                            const qty = selections[cName] || 0;
                            const isSelected = qty > 0;

                            if (group.type === 'text') {
                              return (
                                <div key={ci} className="sf-opt-text">
                                  <label>{cLabel}</label>
                                  <input
                                    type="text"
                                    className="sf-opt-text-input"
                                    placeholder={cLabel}
                                    onChange={(e) => {
                                      setModalOptions(prev => ({
                                        ...prev,
                                        [group.name]: { ...prev[group.name], [`_text_${ci}`]: e.target.value ? 1 : 0 }
                                      }));
                                    }}
                                  />
                                </div>
                              );
                            }

                            return (
                              <div
                                key={ci}
                                className={`sf-opt-choice ${isSelected ? 'sf-opt-choice-selected' : ''}`}
                                onClick={() => {
                                  if (group.type === 'radio' || group.type === 'select') {
                                    toggleRadio(group.name, cName);
                                  } else {
                                    toggleCheckbox(group.name, cName, group.max_selections || 0);
                                  }
                                }}
                              >
                                <div className="sf-opt-choice-info">
                                  <span className="sf-opt-choice-name">{cLabel}</span>
                                  {cPrice > 0 && (
                                    <span className="sf-opt-choice-price" style={{ color: sto.primary_color }}>
                                      {store.currency_symbol} {cPrice.toFixed(3)} +
                                    </span>
                                  )}
                                </div>
                                <div className="sf-opt-choice-control">
                                  {group.allow_quantity && isSelected ? (
                                    <div className="sf-opt-qty" onClick={e => e.stopPropagation()}>
                                      <button onClick={() => setOptionQty(group.name, cName, qty - 1, choice.max_qty || 99)}>
                                        <FiMinus size={12} />
                                      </button>
                                      <span>{qty}</span>
                                      <button onClick={() => setOptionQty(group.name, cName, qty + 1, choice.max_qty || 99)}>
                                        <FiPlus size={12} />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className={`sf-opt-indicator ${group.type === 'radio' || group.type === 'select' ? 'sf-opt-radio' : 'sf-opt-check'} ${isSelected ? 'sf-opt-active' : ''}`}>
                                      {isSelected && <FiCheck size={12} />}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notes */}
              {selectedProduct.option_groups?.length > 0 && (
                <div className="sf-opt-notes">
                  <textarea
                    className="sf-opt-notes-input"
                    placeholder={t('Special instructions...', 'ملاحظات خاصة...')}
                    value={modalNotes}
                    onChange={e => setModalNotes(e.target.value)}
                    rows={2}
                  />
                </div>
              )}

              <div className="sf-modal-price">
                <span className="sf-price">{store.currency_symbol} {(Number(selectedProduct.price) + getModalOptionsTotal(selectedProduct)).toFixed(3)}</span>
                {selectedProduct.compare_price && (
                  <span className="sf-compare-price">{store.currency_symbol} {Number(selectedProduct.compare_price).toFixed(3)}</span>
                )}
              </div>
              <button
                className="sf-add-to-cart-btn"
                onClick={() => { addToCart(selectedProduct); if (!validateOptions(selectedProduct)) setSelectedProduct(null); }}
                disabled={!store.is_open}
                style={{ background: sto.primary_color }}
              >
                {t('Add to Cart', 'أضف إلى السلة')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && <CartSidebar store={store} lang={lang} t={t} onClose={() => setShowCart(false)} onCheckout={() => {
        if (deliveryAreas.length > 0 && !selectedArea) {
          setShowCart(false);
          setAreaPickerFromCart(true);
          setShowAreaPicker(true);
          return;
        }
        setShowCart(false);
        setShowCheckout(true);
      }} deliveryAreas={deliveryAreas} selectedArea={selectedArea} />}

      {/* Area Picker Modal */}
      {showAreaPicker && (
        <AreaPickerModal
          areas={deliveryAreas}
          selectedArea={selectedArea}
          lang={lang}
          t={t}
          store={store}
          locationStatus={locationStatus}
          onDetectLocation={async () => {
            const match = await detectAreaFromLocation(deliveryAreas);
            if (match) {
              setSelectedArea(match);
              setShowAreaPicker(false);
              if (areaPickerFromCart) { setAreaPickerFromCart(false); setShowCheckout(true); }
            }
          }}
          onSelect={(area: any) => { setSelectedArea(area); setShowAreaPicker(false); if (areaPickerFromCart) { setAreaPickerFromCart(false); setShowCheckout(true); } }}
          onClose={() => setShowAreaPicker(false)}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && <CheckoutModal store={store} slug={slug} lang={lang} t={t} onClose={() => setShowCheckout(false)} selectedArea={selectedArea} branches={branches} customer={customer} customerToken={customerToken} onLogin={customerLogin} onRegister={customerRegister} />}

      {/* Auth Modal */}
      {showAuthModal && (
        <CustomerAuthModal
          store={store}
          slug={slug}
          lang={lang}
          t={t}
          customer={customer}
          customerToken={customerToken}
          deliveryAreas={deliveryAreas}
          selectedArea={selectedArea}
          onClose={() => setShowAuthModal(false)}
          onLogin={customerLogin}
          onRegister={customerRegister}
          onLogout={customerLogout}
        />
      )}

      {/* Payment Result Modal */}
      {paymentResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setPaymentResult(null)}>
          <div style={{
            background: '#fff', borderRadius: 16, maxWidth: 420, width: '100%',
            padding: '40px 28px', textAlign: 'center', position: 'relative',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPaymentResult(null)} style={{
              position: 'absolute', top: 12, right: 12, background: 'none',
              border: 'none', fontSize: 20, cursor: 'pointer', color: '#999', padding: 4,
            }}><FiX /></button>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: paymentResult.status === 'success' ? '#dcfce7' : paymentResult.status === 'cancelled' ? '#fef3c7' : '#fee2e2',
              fontSize: 36,
            }}>
              {paymentResult.status === 'success' ? '\u2705' : paymentResult.status === 'cancelled' ? '\u26A0\uFE0F' : '\u274C'}
            </div>
            <h2 style={{ marginBottom: 8, fontSize: 20, fontWeight: 700 }}>
              {paymentResult.status === 'success'
                ? t('Payment Successful!', '\u062A\u0645 \u0627\u0644\u062F\u0641\u0639 \u0628\u0646\u062C\u0627\u062D!')
                : paymentResult.status === 'cancelled'
                  ? t('Payment Cancelled', '\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062F\u0641\u0639')
                  : t('Payment Failed', '\u0641\u0634\u0644 \u0627\u0644\u062F\u0641\u0639')}
            </h2>
            <p style={{ color: '#666', marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
              {paymentResult.status === 'success'
                ? t('Your order has been confirmed and payment received.', '\u062A\u0645 \u062A\u0623\u0643\u064A\u062F \u0637\u0644\u0628\u0643 \u0648\u0627\u0633\u062A\u0644\u0627\u0645 \u0627\u0644\u062F\u0641\u0639.')
                : paymentResult.status === 'cancelled'
                  ? t('You cancelled the payment. Your order is saved.', '\u062A\u0645 \u0625\u0644\u063A\u0627\u0621 \u0627\u0644\u062F\u0641\u0639. \u062A\u0645 \u062D\u0641\u0638 \u0637\u0644\u0628\u0643.')
                  : t('Payment could not be processed. Your order is saved.', '\u062A\u0639\u0630\u0631 \u0625\u062A\u0645\u0627\u0645 \u0627\u0644\u062F\u0641\u0639. \u062A\u0645 \u062D\u0641\u0638 \u0637\u0644\u0628\u0643.')}
            </p>
            {paymentResult.orderNumber && (
              <p style={{ marginBottom: 16, fontWeight: 600, fontSize: 14 }}>
                {t('Order', '\u0627\u0644\u0637\u0644\u0628')}: #{paymentResult.orderNumber}
              </p>
            )}
            {paymentResult.status !== 'success' && paymentResult.error && (
              <p style={{ marginBottom: 6, color: '#dc2626', fontSize: 12 }}>
                {t('Error', '\u062E\u0637\u0623')}: {paymentResult.error}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
              {paymentResult.status !== 'success' && paymentResult.orderNumber && paymentResult.gateway && (
                <button onClick={handleRetryPayment} style={{
                  padding: '10px 28px', background: sto.primary_color || '#2563eb', color: '#fff',
                  borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}>
                  {t('Retry Payment', '\u0625\u0639\u0627\u062F\u0629 \u0627\u0644\u062F\u0641\u0639')}
                </button>
              )}
              <button onClick={() => setPaymentResult(null)} style={{
                padding: '10px 28px',
                background: paymentResult.status === 'success' ? (sto.primary_color || '#2563eb') : 'transparent',
                color: paymentResult.status === 'success' ? '#fff' : (sto.primary_color || '#2563eb'),
                border: paymentResult.status === 'success' ? 'none' : `1px solid ${sto.primary_color || '#2563eb'}`,
                borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>
                {t('Continue Shopping', '\u0645\u062A\u0627\u0628\u0639\u0629 \u0627\u0644\u062A\u0633\u0648\u0642')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Cart Button (desktop only) */}
      {cart.getItemCount() > 0 && !showCart && (
        <button className="sf-floating-cart" onClick={() => setShowCart(true)} style={{ background: sto.primary_color }}>
          <FiShoppingBag />
          <span>{cart.getItemCount()} {t('items', 'عنصر')} - {store.currency_symbol} {cart.getTotal().toFixed(3)}</span>
        </button>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="sf-mobile-bottom-nav" style={isThemeEditor ? { display: 'none' } : undefined}>
        <button className={`sf-bottom-nav-item ${!showProductPage && !searchQuery ? 'active' : ''}`} onClick={() => { if (showProductPage) navigate(storeBase || '/'); setSearchQuery(''); }}>
          <FiHome />
          <span>{t('Home', 'الرئيسية')}</span>
        </button>
        <button className={`sf-bottom-nav-item ${showMobileSearch ? 'active' : ''}`} onClick={() => { if (showProductPage) { navigate(storeBase || '/'); setShowMobileSearch(true); setTimeout(() => { const el = document.querySelector<HTMLInputElement>('.sf-mobile-search-bar input, .sf-food-search input'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); } }, 300); } else { const el = document.querySelector<HTMLInputElement>('.sf-food-search input'); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); } else { setShowMobileSearch(prev => !prev); setTimeout(() => { const inp = document.querySelector<HTMLInputElement>('.sf-mobile-search-bar input'); if (inp) { inp.scrollIntoView({ behavior: 'smooth', block: 'center' }); inp.focus(); } }, 100); } } }}>
          <FiSearch />
          <span>{t('Search', 'بحث')}</span>
        </button>
        <button className="sf-bottom-nav-item" onClick={() => setShowAuthModal(true)}>
          <FiUser />
          <span>{t('Account', 'حسابي')}</span>
        </button>
        <button className="sf-bottom-nav-item sf-bottom-nav-cart" onClick={() => setShowCart(true)}>
          <FiShoppingBag />
          {cart.getItemCount() > 0 && <span className="sf-bottom-nav-badge">{cart.getItemCount()}</span>}
          <span>{t('Cart', 'السلة')}</span>
        </button>
      </nav>
    </div>
  );
}

function FiPackage(props: any) {
  return <FiShoppingBag {...props} />;
}

function CustomerAuthModal({ store, slug, lang, t, customer, customerToken, deliveryAreas = [], selectedArea, onClose, onLogin, onRegister, onLogout }: any) {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(customer ? 'profile' as any : 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', password: '', password_confirmation: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [showOrders, setShowOrders] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [addrForm, setAddrForm] = useState({ label: 'Home', address: '', area: '', block: '', street: '', building: '', floor: '', apartment: '', notes: '' });
  const [showPassword, setShowPassword] = useState(false);

  const translateError = (msg: string) => {
    if (lang !== 'ar') return msg;
    const map: Record<string, string> = {
      'Invalid credentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'Invalid credentials.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'The email has already been taken.': 'البريد الإلكتروني مسجل مسبقاً',
      'These credentials do not match our records.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'The password field must be at least 6 characters.': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
      'The password confirmation does not match.': 'تأكيد كلمة المرور غير متطابق',
    };
    return map[msg] || msg;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onLogin(loginForm.email, loginForm.password);
      onClose();
      toast.success(lang === 'ar' ? 'تم تسجيل الدخول' : 'Logged in successfully');
    } catch (err: any) {
      setError(translateError(err.response?.data?.message || (lang === 'ar' ? 'خطأ في تسجيل الدخول' : 'Login failed')));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onRegister(regForm.name, regForm.email, regForm.phone, regForm.password, regForm.password_confirmation);
      onClose();
      toast.success(lang === 'ar' ? 'تم إنشاء الحساب' : 'Account created successfully');
    } catch (err: any) {
      setError(translateError(err.response?.data?.message || (lang === 'ar' ? 'خطأ في التسجيل' : 'Registration failed')));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.post(`/storefront/${slug}/customers/forgot-password`, { email: forgotEmail });
      setSuccessMsg(lang === 'ar' ? 'تم إرسال كلمة المرور الجديدة إلى بريدك الإلكتروني' : 'New password has been sent to your email');
    } catch (err: any) {
      setError(translateError(err.response?.data?.message || (lang === 'ar' ? 'حدث خطأ' : 'An error occurred')));
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get(`/storefront/${slug}/customer/orders`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      setOrders(res.data);
    } catch {} finally {
      setOrdersLoading(false);
    }
  };

  const loadAddresses = async () => {
    setAddressesLoading(true);
    try {
      const res = await api.get(`/storefront/${slug}/customer/addresses`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      setAddresses(res.data);
    } catch {} finally {
      setAddressesLoading(false);
    }
  };

  const saveAddress = async () => {
    try {
      const payload = {
        ...addrForm,
        address: addrForm.address || [addrForm.area, addrForm.block && `Block ${addrForm.block}`, addrForm.street && `St ${addrForm.street}`, addrForm.building && `Bldg ${addrForm.building}`].filter(Boolean).join(', '),
      };
      if (editingAddress) {
        await api.put(`/storefront/${slug}/customer/addresses/${editingAddress.id}`, payload, {
          headers: { Authorization: `Bearer ${customerToken}` },
        });
      } else {
        await api.post(`/storefront/${slug}/customer/addresses`, payload, {
          headers: { Authorization: `Bearer ${customerToken}` },
        });
      }
      setShowAddressForm(false);
      setEditingAddress(null);
      loadAddresses();
      toast.success(lang === 'ar' ? 'تم حفظ العنوان' : 'Address saved');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Error';
      toast.error(translateError(msg));
    }
  };

  const deleteAddress = async (id: number) => {
    try {
      await api.delete(`/storefront/${slug}/customer/addresses/${id}`, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      loadAddresses();
      toast.success(lang === 'ar' ? 'تم حذف العنوان' : 'Address deleted');
    } catch {}
  };

  const openAddressForm = (addr?: any) => {
    if (addr) {
      setEditingAddress(addr);
      setAddrForm({ label: addr.label || 'Home', address: addr.address || '', area: addr.area || '', block: addr.block || '', street: addr.street || '', building: addr.building || '', floor: addr.floor || '', apartment: addr.apartment || '', notes: addr.notes || '' });
    } else {
      setEditingAddress(null);
      const autoArea = selectedArea ? (lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name)) : '';
      setAddrForm({ label: 'Home', address: '', area: autoArea, block: '', street: '', building: '', floor: '', apartment: '', notes: '' });
    }
    setShowAddressForm(true);
  };

  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  const statusLabel = (s: string) => {
    const map: Record<string, [string, string]> = {
      pending: ['Pending', 'قيد الانتظار'],
      confirmed: ['Confirmed', 'مؤكد'],
      preparing: ['Preparing', 'قيد التحضير'],
      ready: ['Ready', 'جاهز'],
      out_for_delivery: ['Out for Delivery', 'في الطريق'],
      delivered: ['Delivered', 'تم التوصيل'],
      cancelled: ['Cancelled', 'ملغي'],
    };
    const [en, ar] = map[s] || [s, s];
    return lang === 'ar' ? ar : en;
  };

  const paymentStatusLabel = (s: string) => {
    const map: Record<string, [string, string]> = {
      pending: ['Unpaid', 'غير مدفوع'],
      paid: ['Paid', 'مدفوع'],
      failed: ['Failed', 'فشل الدفع'],
    };
    const [en, ar] = map[s] || [s, s];
    return lang === 'ar' ? ar : en;
  };

  const paymentStatusColor = (s: string) => {
    if (s === 'paid') return '#22c55e';
    if (s === 'failed') return '#ef4444';
    return '#f59e0b';
  };

  const statusColor = (s: string) => {
    if (s === 'delivered') return '#22c55e';
    if (s === 'cancelled') return '#ef4444';
    if (s === 'pending') return '#f59e0b';
    return store.primary_color;
  };

  // If customer is logged in, show profile
  if (customer) {
    return (
      <div className="sf-modal-overlay" onClick={onClose}>
        <div className="sf-auth-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: showOrders || showAddresses ? 520 : 420 }}>
          <button className="sf-modal-close" onClick={onClose}><FiX /></button>

          {showAddresses ? (
            showAddressForm ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <button onClick={() => { setShowAddressForm(false); setEditingAddress(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4, fontFamily: 'inherit' }}>{lang === 'ar' ? '→' : '←'}</button>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{editingAddress ? t('Edit Address', 'تعديل العنوان') : t('Add Address', 'إضافة عنوان')}</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
                  <div className="sf-auth-field">
                    <label>{t('Label', 'التسمية')}</label>
                    <select value={addrForm.label} onChange={(e) => setAddrForm({ ...addrForm, label: e.target.value })} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 13 }}>
                      <option value="Home">{t('Home', 'المنزل')}</option>
                      <option value="Work">{t('Work', 'العمل')}</option>
                      <option value="Other">{t('Other', 'أخرى')}</option>
                    </select>
                  </div>
                  <div className="sf-auth-field">
                    <label>{t('Area', 'المنطقة')}</label>
                    {deliveryAreas.length > 0 ? (
                      <select value={addrForm.area} onChange={(e) => setAddrForm({ ...addrForm, area: e.target.value })} style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 13 }}>
                        <option value="">{t('Select area', 'اختر المنطقة')}</option>
                        {deliveryAreas.map((da: any) => (
                          <option key={da.id} value={lang === 'ar' ? da.name : (da.name_en || da.name)}>
                            {lang === 'ar' ? da.name : (da.name_en || da.name)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input value={addrForm.area} onChange={(e) => setAddrForm({ ...addrForm, area: e.target.value })} placeholder={t('e.g. Salmiya', 'مثال: السالمية')} />
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div className="sf-auth-field">
                      <label>{t('Block', 'القطعة')}</label>
                      <input value={addrForm.block} onChange={(e) => setAddrForm({ ...addrForm, block: e.target.value })} placeholder={t('Block', 'القطعة')} />
                    </div>
                    <div className="sf-auth-field">
                      <label>{t('Street', 'الشارع')}</label>
                      <input value={addrForm.street} onChange={(e) => setAddrForm({ ...addrForm, street: e.target.value })} placeholder={t('Street', 'الشارع')} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div className="sf-auth-field">
                      <label>{t('Building', 'المبنى')}</label>
                      <input value={addrForm.building} onChange={(e) => setAddrForm({ ...addrForm, building: e.target.value })} placeholder={t('Building', 'المبنى')} />
                    </div>
                    <div className="sf-auth-field">
                      <label>{t('Floor', 'الطابق')}</label>
                      <input value={addrForm.floor} onChange={(e) => setAddrForm({ ...addrForm, floor: e.target.value })} placeholder={t('Floor', 'الطابق')} />
                    </div>
                    <div className="sf-auth-field">
                      <label>{t('Apt', 'الشقة')}</label>
                      <input value={addrForm.apartment} onChange={(e) => setAddrForm({ ...addrForm, apartment: e.target.value })} placeholder={t('Apt', 'الشقة')} />
                    </div>
                  </div>
                  <div className="sf-auth-field">
                    <label>{t('Notes', 'ملاحظات')}</label>
                    <input value={addrForm.notes} onChange={(e) => setAddrForm({ ...addrForm, notes: e.target.value })} placeholder={t('Delivery notes', 'ملاحظات التوصيل')} />
                  </div>
                  <button onClick={saveAddress} className="sf-auth-submit" style={{ background: store.primary_color, marginTop: 4 }}>
                    {editingAddress ? t('Update Address', 'تحديث العنوان') : t('Save Address', 'حفظ العنوان')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={() => setShowAddresses(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4, fontFamily: 'inherit' }}>{lang === 'ar' ? '→' : '←'}</button>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{t('My Addresses', 'عناويني')}</h3>
                  </div>
                  <button onClick={() => openAddressForm()} style={{ background: store.primary_color, color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>
                    + {t('Add', 'إضافة')}
                  </button>
                </div>
                {addressesLoading ? (
                  <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>{t('Loading...', 'جاري التحميل...')}</div>
                ) : addresses.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>{t('No addresses yet', 'لا توجد عناوين بعد')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
                    {addresses.map((addr: any) => (
                      <div key={addr.id} style={{ padding: 12, border: '1px solid var(--gray-100)', borderRadius: 6, position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <strong style={{ fontSize: 13 }}>{addr.label}</strong>
                              {addr.is_default && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 50, background: store.primary_color + '15', color: store.primary_color, fontWeight: 600 }}>{t('Default', 'افتراضي')}</span>}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.5 }}>
                              {[addr.area, addr.block && `${t('Block', 'ق')} ${addr.block}`, addr.street && `${t('St', 'ش')} ${addr.street}`, addr.building && `${t('Bldg', 'مبنى')} ${addr.building}`, addr.floor && `${t('Floor', 'طابق')} ${addr.floor}`, addr.apartment && `${t('Apt', 'شقة')} ${addr.apartment}`].filter(Boolean).join(', ')}
                            </div>
                            {addr.notes && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{addr.notes}</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => openAddressForm(addr)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}><FiEdit2 size={14} /></button>
                            <button onClick={() => deleteAddress(addr.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e63946', padding: 4 }}><FiTrash2 size={14} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )
          ) : showOrders ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <button onClick={() => setShowOrders(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4, fontFamily: 'inherit' }}>{lang === 'ar' ? '→' : '←'}</button>
                <h3 style={{ margin: 0, fontSize: 16 }}>{t('My Orders', 'طلباتي')}</h3>
              </div>
              {ordersLoading ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>{t('Loading...', 'جاري التحميل...')}</div>
              ) : orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: 'var(--gray-400)' }}>{t('No orders yet', 'لا توجد طلبات بعد')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '60vh', overflowY: 'auto' }}>
                  {orders.map((order: any) => (
                    <div key={order.id} style={{ border: '1px solid var(--gray-100)', borderRadius: 8, overflow: 'hidden' }}>
                      <div
                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        style={{ padding: 12, cursor: 'pointer', background: expandedOrder === order.id ? 'var(--gray-50, #f9fafb)' : 'transparent' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <strong style={{ fontSize: 13 }}>{order.order_number}</strong>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {order.payment_method !== 'cash' && (
                              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 50, background: paymentStatusColor(order.payment_status) + '15', color: paymentStatusColor(order.payment_status), fontWeight: 600 }}>
                                {paymentStatusLabel(order.payment_status)}
                              </span>
                            )}
                            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 50, background: statusColor(order.status) + '15', color: statusColor(order.status), fontWeight: 600 }}>
                              {statusLabel(order.status)}
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>
                          {new Date(order.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {order.items?.length || 0} {t('items', 'عناصر')} · <strong style={{ color: 'var(--gray-700)' }}>{store.currency_symbol} {Number(order.total).toFixed(3)}</strong>
                          </span>
                          <FiChevronDown size={14} style={{ color: 'var(--gray-400)', transition: 'transform 0.2s', transform: expandedOrder === order.id ? 'rotate(180deg)' : 'none' }} />
                        </div>
                      </div>
                      {expandedOrder === order.id && order.items && (
                        <div style={{ borderTop: '1px solid var(--gray-100)', padding: '10px 12px', background: 'var(--gray-50, #f9fafb)' }}>
                          {order.items.map((item: any, idx: number) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: idx < order.items.length - 1 ? '1px solid var(--gray-100)' : 'none' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{item.product_name}</div>
                                {item.options && (() => {
                                  try {
                                    const opts = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                                    if (Array.isArray(opts) && opts.length) {
                                      return <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{opts.map((o: any) => o.choice || o.name).join(', ')}</div>;
                                    }
                                  } catch {}
                                  return null;
                                })()}
                                {item.notes && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{item.notes}</div>}
                              </div>
                              <div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginInlineStart: 12 }}>
                                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>x{item.quantity}</div>
                                <div style={{ fontSize: 12, fontWeight: 600 }}>{store.currency_symbol} {Number(item.total || item.price * item.quantity).toFixed(3)}</div>
                              </div>
                            </div>
                          ))}
                          {order.delivery_address && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--gray-100)', fontSize: 12, color: 'var(--gray-500)' }}>
                              <FiMapPin size={11} style={{ marginInlineEnd: 4 }} /> {order.delivery_address}
                            </div>
                          )}
                          {order.notes && (
                            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--gray-400)', fontStyle: 'italic' }}>
                              {order.notes}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: store.primary_color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, margin: '0 auto 16px' }}>
                  {customer.name?.charAt(0).toUpperCase()}
                </div>
                <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>{customer.name}</h3>
                <p style={{ color: 'var(--gray-500)', fontSize: 13, margin: 0 }}>{customer.email}</p>
                {customer.phone && <p style={{ color: 'var(--gray-500)', fontSize: 13, margin: '4px 0 0' }}>{customer.phone}</p>}
              </div>
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button onClick={() => { setShowOrders(true); loadOrders(); }} className="sf-auth-menu-item">
                  <FiShoppingBag size={16} /> {t('My Orders', 'طلباتي')}
                </button>
                <button onClick={() => { setShowAddresses(true); loadAddresses(); }} className="sf-auth-menu-item">
                  <FiMapPin size={16} /> {t('My Addresses', 'عناويني')}
                </button>
                <button
                  onClick={() => { onLogout(); onClose(); toast.success(lang === 'ar' ? 'تم تسجيل الخروج' : 'Logged out'); }}
                  className="sf-auth-menu-item"
                  style={{ color: 'var(--danger, #e63946)' }}
                >
                  <FiLogOut size={16} /> {t('Logout', 'تسجيل الخروج')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sf-modal-overlay" onClick={onClose}>
      <div className="sf-auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sf-modal-close" onClick={onClose}><FiX /></button>

        {/* Tabs */}
        <div className="sf-auth-tabs">
          <button className={`sf-auth-tab ${mode === 'login' || mode === 'forgot' ? 'active' : ''}`} onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }}>
            {t('Login', 'تسجيل الدخول')}
          </button>
          <button className={`sf-auth-tab ${mode === 'register' ? 'active' : ''}`} onClick={() => { setMode('register'); setError(''); setSuccessMsg(''); }}>
            {t('Register', 'إنشاء حساب')}
          </button>
        </div>

        {error && <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {mode === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="sf-auth-field">
              <label>{t('Email', 'البريد الإلكتروني')}</label>
              <input type="email" dir="ltr" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder={t('Enter your email', 'أدخل بريدك الإلكتروني')} />
            </div>
            <div className="sf-auth-field">
              <label>{t('Password', 'كلمة المرور')}</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} dir="ltr" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder={t('Enter your password', 'أدخل كلمة المرور')} style={{ paddingInlineEnd: 40 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}>
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" className="sf-auth-submit" style={{ background: store.primary_color }} disabled={loading}>
              {loading ? (t('Logging in...', 'جاري تسجيل الدخول...')) : t('Login', 'تسجيل الدخول')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('forgot'); setError(''); setSuccessMsg(''); setForgotEmail(loginForm.email); }}
              style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--gray-400)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {t('Forgot password?', 'نسيت كلمة المرور؟')}
            </button>
          </form>
        ) : mode === 'forgot' ? (
          <form onSubmit={handleForgotPassword}>
            <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
              {t('Enter your email and we\'ll send you a new password.', 'أدخل بريدك الإلكتروني وسنرسل لك كلمة مرور جديدة.')}
            </p>
            {successMsg && <div style={{ background: '#dcfce7', color: '#16a34a', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{successMsg}</div>}
            <div className="sf-auth-field">
              <label>{t('Email', 'البريد الإلكتروني')}</label>
              <input type="email" dir="ltr" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder={t('Enter your email', 'أدخل بريدك الإلكتروني')} />
            </div>
            <button type="submit" className="sf-auth-submit" style={{ background: store.primary_color }} disabled={loading}>
              {loading ? '...' : t('Send New Password', 'إرسال كلمة مرور جديدة')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); setSuccessMsg(''); }}
              style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--gray-400)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {t('Back to login', 'العودة لتسجيل الدخول')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div className="sf-auth-field">
              <label>{t('Full Name', 'الاسم الكامل')} *</label>
              <input type="text" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} placeholder={t('Enter your name', 'أدخل اسمك')} />
            </div>
            <div className="sf-auth-field">
              <label>{t('Email', 'البريد الإلكتروني')} *</label>
              <input type="email" dir="ltr" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder={t('Enter your email', 'أدخل بريدك الإلكتروني')} />
            </div>
            <div className="sf-auth-field">
              <label>{t('Phone', 'الهاتف')}</label>
              <input type="tel" dir="ltr" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: toEnDigits(e.target.value) })} placeholder={t('Enter your phone', 'أدخل رقم هاتفك')} />
            </div>
            <div className="sf-auth-field">
              <label>{t('Password', 'كلمة المرور')} *</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} dir="ltr" required minLength={6} value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder={t('Min 6 characters', 'على الأقل 6 أحرف')} style={{ paddingInlineEnd: 40 }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}>
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
            <div className="sf-auth-field">
              <label>{t('Confirm Password', 'تأكيد كلمة المرور')} *</label>
              <input type="password" dir="ltr" required value={regForm.password_confirmation} onChange={(e) => setRegForm({ ...regForm, password_confirmation: e.target.value })} placeholder={t('Confirm your password', 'أكد كلمة المرور')} />
            </div>
            <button type="submit" className="sf-auth-submit" style={{ background: store.primary_color }} disabled={loading}>
              {loading ? (t('Creating account...', 'جاري إنشاء الحساب...')) : t('Create Account', 'إنشاء حساب')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function CartSidebar({ store, lang, t, onClose, onCheckout, deliveryAreas = [], selectedArea }: any) {
  const cart = useCartStore();

  return (
    <div className="sf-cart-overlay" onClick={onClose}>
      <div className="sf-cart-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="sf-cart-header">
          <h2><FiShoppingBag /> {t('Your Cart', 'سلتك')}</h2>
          <button onClick={onClose}><FiX /></button>
        </div>

        {cart.items.length === 0 ? (
          <div className="sf-cart-empty">
            <FiShoppingBag size={48} />
            <p>{t('Your cart is empty', 'سلتك فارغة')}</p>
          </div>
        ) : (
          <>
            <div className="sf-cart-items">
              {cart.items.map((item) => {
                const optTotal = item.selectedOptions?.reduce((s, o) => s + o.price * o.quantity, 0) || 0;
                return (
                  <div key={item.cartKey} className="sf-cart-item">
                    {item.image && (
                      <img src={`${STORAGE_URL}/${item.image}`} alt="" className="sf-cart-item-img" />
                    )}
                    <div className="sf-cart-item-info">
                      <h4>{item.name}</h4>
                      {item.selectedOptions?.map((opt, oi) => (
                        <div key={oi} className="sf-cart-item-opt">
                          {opt.choiceName}{opt.quantity > 1 ? ` x${opt.quantity}` : ''}
                          {opt.price > 0 && <span> (+{store.currency_symbol} {(opt.price * opt.quantity).toFixed(3)})</span>}
                        </div>
                      ))}
                      <span className="sf-cart-item-price">{store.currency_symbol} {(item.price + optTotal).toFixed(3)}</span>
                    </div>
                    <div className="sf-cart-item-qty">
                      <button onClick={() => cart.updateQuantity(item.cartKey, item.quantity - 1)}><FiMinus /></button>
                      <span>{item.quantity}</span>
                      <button onClick={() => cart.updateQuantity(item.cartKey, item.quantity + 1)}><FiPlus /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="sf-cart-footer">
              {/* Free delivery progress bar */}
              {(() => {
                const threshold = selectedArea?.free_delivery_threshold ? parseFloat(selectedArea.free_delivery_threshold) : 0;
                if (threshold <= 0) return null;
                const subtotal = cart.getTotal();
                const remaining = Math.max(0, threshold - subtotal);
                const progress = Math.min(100, (subtotal / threshold) * 100);
                const isFree = subtotal >= threshold;
                return (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, marginBottom: 6, textAlign: 'center', color: isFree ? '#16a34a' : 'var(--gray-500)' }}>
                      {isFree
                        ? `🎉 ${t('You got free delivery!', 'حصلت على توصيل مجاني!')}`
                        : `${t('Add', 'أضف')} ${store.currency_symbol} ${remaining.toFixed(3)} ${t('more for free delivery', 'للحصول على توصيل مجاني')}`
                      }
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--gray-100)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 3,
                        width: `${progress}%`,
                        background: isFree ? '#16a34a' : store.primary_color,
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </div>
                );
              })()}
              <div className="sf-cart-total">
                <span>{t('Subtotal', 'المجموع')}</span>
                <strong>{store.currency_symbol} {cart.getTotal().toFixed(3)}</strong>
              </div>
              <button className="sf-checkout-btn" onClick={onCheckout} style={{ background: store.primary_color }}>
                {deliveryAreas.length > 0 && !selectedArea
                  ? t('Select Area & Checkout', 'اختر المنطقة وأكمل الطلب')
                  : t('Checkout', 'إتمام الطلب')}
              </button>
              <button className="sf-clear-cart" onClick={() => cart.clearCart()}>
                {t('Clear Cart', 'إفراغ السلة')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AreaPickerModal({ areas, selectedArea, lang, t, store, locationStatus, onDetectLocation, onSelect, onClose }: any) {
  const [search, setSearch] = useState('');
  const filtered = areas.filter((a: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.name_en || '').toLowerCase().includes(q);
  });

  const showLocationPrompt = locationStatus !== 'granted';

  return (
    <div className="sf-area-picker-overlay" onClick={onClose}>
      <div className="sf-area-picker" onClick={(e) => e.stopPropagation()}>
        <div className="sf-area-picker-header">
          <h3>{t('Select Area', 'اختر المنطقة')}</h3>
          <button onClick={onClose}><FiX /></button>
        </div>

        {/* Location detect prompt */}
        {showLocationPrompt && (
          <div style={{
            margin: '12px 16px 0', padding: '14px 16px', borderRadius: 12,
            background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', border: '1px solid #bae6fd',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', background: store?.primary_color || '#3b82f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FiMapPin style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f', marginBottom: 2 }}>
                {t('Detect my location', 'اكتشف موقعي')}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {locationStatus === 'denied'
                  ? t('Location was blocked. Please enable it in your browser settings and try again.', 'تم حظر الموقع. يرجى تفعيله من إعدادات المتصفح والمحاولة مرة أخرى.')
                  : t('Allow location access to auto-detect your area', 'اسمح بالوصول للموقع لاكتشاف منطقتك تلقائياً')}
              </div>
            </div>
            <button
              onClick={onDetectLocation}
              disabled={locationStatus === 'detecting'}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 12,
                background: store?.primary_color || '#3b82f6', color: '#fff', cursor: 'pointer',
                opacity: locationStatus === 'detecting' ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {locationStatus === 'detecting'
                ? t('Detecting...', 'جاري الكشف...')
                : t('Enable', 'تفعيل')}
            </button>
          </div>
        )}

        <div className="sf-area-picker-search">
          <FiSearch />
          <input
            type="text"
            placeholder={t('Search areas...', 'البحث عن مناطق...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="sf-area-picker-list">
          {filtered.map((area: any) => (
            <div
              key={area.id}
              className={`sf-area-picker-item ${selectedArea?.id === area.id ? 'selected' : ''}`}
              onClick={() => onSelect(area)}
            >
              <span>{lang === 'ar' ? area.name : (area.name_en || area.name)}</span>
              {area.estimated_time && (
                <span className="sf-area-picker-fee">
                  {area.estimated_time} {area.estimated_time_unit === 'days' ? (area.business_days ? t('business days', 'يوم عمل') : t('days', 'يوم')) : area.estimated_time_unit === 'hours' ? t('hours', 'ساعة') : t('min', 'دقيقة')}
                </span>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
              {t('No areas found', 'لا توجد مناطق')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckoutModal({ store, slug, lang, t, onClose, selectedArea, branches = [], customer, customerToken, onLogin, onRegister }: any) {
  const cart = useCartStore();
  const pickupAvailable = store.pickup_enabled && branches.length > 0;
  const deliveryAvailable = store.delivery_enabled;
  const defaultType = deliveryAvailable ? 'delivery' : (pickupAvailable ? 'pickup' : 'delivery');
  const [checkoutMode, setCheckoutMode] = useState<'choose' | 'guest' | 'auth' | 'form'>(customer ? 'form' : 'choose');
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', password: '', password_confirmation: '' });
  const [form, setForm] = useState({
    customer_name: customer?.name || '',
    customer_email: customer?.email || '',
    customer_phone: customer?.phone || '',
    delivery_address: customer?.address || '',
    addr_block: '',
    addr_street: '',
    addr_house: '',
    addr_floor: '',
    addr_apartment: '',
    addr_notes: '',
    delivery_area: '',
    type: defaultType, payment_method: 'cash', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoValid, setPromoValid] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentGateways, setPaymentGateways] = useState<any[]>([]);
  const [paymentRedirect, setPaymentRedirect] = useState<any>(null);
  const [customerAddresses, setCustomerAddresses] = useState<any[]>([]);
  const [showPwd, setShowPwd] = useState(false);

  const translateErr = (msg: string) => {
    if (lang !== 'ar') return msg;
    const map: Record<string, string> = {
      'Invalid credentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'Invalid credentials.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'The email has already been taken.': 'البريد الإلكتروني مسجل مسبقاً',
      'These credentials do not match our records.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      'The password field must be at least 6 characters.': 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
      'The password confirmation does not match.': 'تأكيد كلمة المرور غير متطابق',
    };
    return map[msg] || msg;
  };

  useEffect(() => {
    if (customer && customerToken) {
      api.get(`/storefront/${slug}/customer/addresses`, { headers: { Authorization: `Bearer ${customerToken}` } })
        .then((res) => {
          const addrs = res.data || [];
          setCustomerAddresses(addrs);
          // Auto-fill default address
          const def = addrs.find((a: any) => a.is_default) || addrs[0];
          if (def) {
            setForm(prev => ({
              ...prev,
              addr_block: def.block || '',
              addr_street: def.street || '',
              addr_house: def.building || '',
              addr_floor: def.floor || '',
              addr_apartment: def.apartment || '',
              addr_notes: def.notes || '',
            }));
          }
        })
        .catch(() => {});
    }
  }, [customer, customerToken, slug]);

  const handleAuthLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const c = await onLogin(loginForm.email, loginForm.password);
      setForm({ ...form, customer_name: c.name, customer_email: c.email, customer_phone: c.phone || '', delivery_address: c.address || '' });
      setCheckoutMode('form');
      toast.success(lang === 'ar' ? 'تم تسجيل الدخول' : 'Logged in');
    } catch (err: any) {
      setAuthError(translateErr(err.response?.data?.message || (lang === 'ar' ? 'خطأ في تسجيل الدخول' : 'Login failed')));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const c = await onRegister(regForm.name, regForm.email, regForm.phone, regForm.password, regForm.password_confirmation);
      setForm({ ...form, customer_name: c.name, customer_email: c.email, customer_phone: c.phone || '', delivery_address: c.address || '' });
      setCheckoutMode('form');
      toast.success(lang === 'ar' ? 'تم إنشاء الحساب' : 'Account created');
    } catch (err: any) {
      setAuthError(translateErr(err.response?.data?.message || (lang === 'ar' ? 'خطأ في التسجيل' : 'Registration failed')));
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    api.get(`/storefront/${slug}/payment-gateways`).then((res) => {
      const gws = res.data || [];
      setPaymentGateways(gws);
      if (gws.length > 0) setForm((f) => ({ ...f, payment_method: gws[0].gateway }));
    }).catch(() => {});
  }, [slug]);

  const cartSubtotal = cart.getTotal();
  const freeThreshold = selectedArea?.free_delivery_threshold ? parseFloat(selectedArea.free_delivery_threshold) : 0;
  const isFreeDelivery = freeThreshold > 0 && cartSubtotal >= freeThreshold;
  const rawDeliveryFee = form.type === 'delivery'
    ? (selectedArea ? parseFloat(selectedArea.delivery_fee || 0) : parseFloat(store.delivery_fee || 0))
    : 0;
  const deliveryFee = isFreeDelivery ? 0 : rawDeliveryFee;
  const effectiveMinOrder = selectedArea?.minimum_order ? parseFloat(selectedArea.minimum_order) : parseFloat(store.minimum_order || 0);
  const total = cartSubtotal + deliveryFee - promoDiscount;

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await api.post(`/storefront/${slug}/validate-promo`, {
        code: promoCode,
        subtotal: cart.getTotal(),
        items: cart.items.map((item: any) => ({ product_id: item.product_id, quantity: item.quantity })),
      });
      if (res.data.valid) {
        setPromoDiscount(res.data.discount);
        setPromoMessage(res.data.message);
        setPromoValid(true);
      } else {
        setPromoDiscount(0);
        setPromoMessage(res.data.message);
        setPromoValid(false);
      }
    } catch {
      setPromoDiscount(0);
      setPromoMessage(t('Error validating code', 'خطأ في التحقق من الكود'));
      setPromoValid(false);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const isOnlinePayment = form.payment_method !== 'cash';
      const gateway = form.payment_method === 'cash' ? undefined : form.payment_method;

      const headers: any = {};
      if (customerToken) headers.Authorization = `Bearer ${customerToken}`;

      const combinedAddress = [
        form.addr_block && `Block ${form.addr_block}`,
        form.addr_street && `St ${form.addr_street}`,
        form.addr_house && `House ${form.addr_house}`,
        form.addr_floor && `Floor ${form.addr_floor}`,
        form.addr_apartment && `Apt ${form.addr_apartment}`,
        form.addr_notes,
      ].filter(Boolean).join(', ');

      const res = await api.post(`/storefront/${slug}/orders`, {
        ...form,
        delivery_address: combinedAddress || form.delivery_address,
        delivery_address_details: form.type === 'delivery' ? {
          area: selectedArea ? (lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name)) : form.delivery_area,
          block: form.addr_block,
          street: form.addr_street,
          building: form.addr_house,
          floor: form.addr_floor,
          apartment: form.addr_apartment,
          notes: form.addr_notes,
        } : undefined,
        payment_method: isOnlinePayment ? 'online' : 'cash',
        area_id: selectedArea?.id || undefined,
        promo_code: promoValid ? promoCode : undefined,
        lang,
        items: cart.items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          options: item.selectedOptions?.length ? item.selectedOptions : undefined,
        })),
      }, { headers });

      if (isOnlinePayment && gateway) {
        // Initiate payment redirect
        try {
          const payRes = await api.post(`/storefront/${slug}/orders/${encodeURIComponent(res.data.order_number)}/pay`, {
            gateway,
            lang,
            return_url: window.location.origin + `/store/${slug}`,
          });

          if (payRes.data.payment_url && payRes.data.form_data) {
            setPaymentRedirect(payRes.data);
            // Don't clear cart yet — only clear after successful payment callback
            return;
          }
        } catch (payErr: any) {
          // Payment initiation failed, but order is created
          const errMsg = payErr.response?.data?.message || t('Payment initiation failed. Please try again.', 'فشل بدء عملية الدفع. حاول مرة أخرى.');
          toast.error(errMsg);
          setOrderResult({ ...res.data, _paymentFailed: true });
          // Don't clear cart — user may retry payment
          return;
        }
      }

      setOrderResult(res.data);
      cart.clearCart();
      toast.success(t('Order placed successfully!', 'تم تقديم الطلب بنجاح!'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('Error placing order', 'خطأ في تقديم الطلب'));
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit payment redirect form
  useEffect(() => {
    if (paymentRedirect) {
      const timer = setTimeout(() => {
        const form = document.getElementById('knet-redirect-form') as HTMLFormElement;
        if (form) form.submit();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [paymentRedirect]);

  if (paymentRedirect) {
    return (
      <div className="sf-modal-overlay">
        <div className="sf-modal sf-checkout-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 40, marginBottom: 20, animation: 'spin 1s linear infinite' }}>⏳</div>
            <h2>{t('Redirecting to payment...', 'جاري التحويل للدفع...')}</h2>
            <p style={{ color: 'var(--gray-500)', marginTop: 12 }}>
              {t('Please wait, do not close this page', 'يرجى الانتظار، لا تغلق هذه الصفحة')}
            </p>
            <form id="knet-redirect-form" action={paymentRedirect.payment_url} method="POST" style={{ display: 'none' }}>
              {Object.entries(paymentRedirect.form_data).map(([key, value]) => (
                <input key={key} type="hidden" name={key} value={value as string} />
              ))}
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (orderResult) {
    return (
      <div className="sf-modal-overlay" onClick={onClose}>
        <div className="sf-modal sf-checkout-modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 60, marginBottom: 20 }}>{orderResult._paymentFailed ? '⚠' : '✓'}</div>
            <h2>{orderResult._paymentFailed
              ? t('Order Created - Payment Pending', 'تم إنشاء الطلب - الدفع معلق')
              : t('Order Confirmed!', 'تم تأكيد الطلب!')
            }</h2>
            {orderResult._paymentFailed && (
              <p style={{ margin: '12px 0', color: '#e67e22', fontSize: 14 }}>
                {t('Payment could not be initiated. Please contact the store.', 'لم يتم بدء عملية الدفع. يرجى التواصل مع المتجر.')}
              </p>
            )}
            <p style={{ margin: '16px 0', color: 'var(--gray-500)' }}>
              {t('Order Number', 'رقم الطلب')}: <strong>{orderResult.order_number}</strong>
            </p>
            <p style={{ color: 'var(--gray-500)' }}>
              {t('Total', 'المجموع')}: <strong>{store.currency_symbol} {Number(orderResult.total).toFixed(3)}</strong>
            </p>
            <button className="sf-add-to-cart-btn" onClick={onClose} style={{ background: store.primary_color, marginTop: 24 }}>
              {t('Done', 'تم')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Choose mode: guest or login/register
  if (checkoutMode === 'choose') {
    return (
      <div className="sf-modal-overlay" onClick={onClose}>
        <div className="sf-modal sf-checkout-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
          <button className="sf-modal-close" onClick={onClose}><FiX /></button>
          <h2 style={{ textAlign: 'center', marginBottom: 8 }}>{t('Checkout', 'إتمام الطلب')}</h2>
          <p style={{ textAlign: 'center', color: 'var(--gray-500)', fontSize: 13, marginBottom: 24 }}>
            {t('How would you like to continue?', 'كيف تريد المتابعة؟')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              className="sf-checkout-choice-btn"
              onClick={() => setCheckoutMode('form')}
              style={{ border: `2px solid ${store.primary_color}`, color: store.primary_color }}
            >
              <FiUser size={20} />
              <div>
                <strong>{t('Continue as Guest', 'متابعة كزائر')}</strong>
                <span>{t('No account needed', 'بدون حساب')}</span>
              </div>
            </button>
            <button
              className="sf-checkout-choice-btn"
              onClick={() => setCheckoutMode('auth')}
              style={{ background: store.primary_color, color: 'white', border: `2px solid ${store.primary_color}` }}
            >
              <FiLogOut size={20} />
              <div>
                <strong>{t('Login / Register', 'تسجيل الدخول / إنشاء حساب')}</strong>
                <span style={{ opacity: 0.8 }}>{t('Save your info for next time', 'احفظ بياناتك للمرة القادمة')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Auth mode: login or register inside checkout
  if (checkoutMode === 'auth') {
    return (
      <div className="sf-modal-overlay" onClick={onClose}>
        <div className="sf-modal sf-checkout-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
          <button className="sf-modal-close" onClick={onClose}><FiX /></button>

          <div className="sf-auth-tabs">
            <button className={`sf-auth-tab ${authTab === 'login' ? 'active' : ''}`} onClick={() => { setAuthTab('login'); setAuthError(''); }}>
              {t('Login', 'تسجيل الدخول')}
            </button>
            <button className={`sf-auth-tab ${authTab === 'register' ? 'active' : ''}`} onClick={() => { setAuthTab('register'); setAuthError(''); }}>
              {t('Register', 'إنشاء حساب')}
            </button>
          </div>

          {authError && <div style={{ background: '#fee', color: '#c00', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{authError}</div>}

          {authTab === 'login' ? (
            <form onSubmit={handleAuthLogin}>
              <div className="sf-auth-field">
                <label>{t('Email', 'البريد الإلكتروني')}</label>
                <input type="email" dir="ltr" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder={t('Enter your email', 'أدخل بريدك الإلكتروني')} />
              </div>
              <div className="sf-auth-field">
                <label>{t('Password', 'كلمة المرور')}</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} dir="ltr" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder={t('Enter your password', 'أدخل كلمة المرور')} style={{ paddingInlineEnd: 40 }} />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}>
                    {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="sf-auth-submit" style={{ background: store.primary_color }} disabled={authLoading}>
                {authLoading ? t('Logging in...', 'جاري الدخول...') : t('Login & Continue', 'دخول ومتابعة')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleAuthRegister}>
              <div className="sf-auth-field">
                <label>{t('Full Name', 'الاسم الكامل')} *</label>
                <input type="text" required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} placeholder={t('Enter your name', 'أدخل اسمك')} />
              </div>
              <div className="sf-auth-field">
                <label>{t('Email', 'البريد الإلكتروني')} *</label>
                <input type="email" dir="ltr" required value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} placeholder={t('Enter your email', 'أدخل بريدك الإلكتروني')} />
              </div>
              <div className="sf-auth-field">
                <label>{t('Phone', 'الهاتف')}</label>
                <input type="tel" dir="ltr" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: toEnDigits(e.target.value) })} placeholder={t('Enter your phone', 'أدخل رقم هاتفك')} />
              </div>
              <div className="sf-auth-field">
                <label>{t('Password', 'كلمة المرور')} *</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPwd ? 'text' : 'password'} dir="ltr" required minLength={6} value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} placeholder={t('Min 6 characters', 'على الأقل 6 أحرف')} style={{ paddingInlineEnd: 40 }} />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4 }}>
                    {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>
              <div className="sf-auth-field">
                <label>{t('Confirm Password', 'تأكيد كلمة المرور')} *</label>
                <input type="password" dir="ltr" required value={regForm.password_confirmation} onChange={(e) => setRegForm({ ...regForm, password_confirmation: e.target.value })} placeholder={t('Confirm password', 'أكد كلمة المرور')} />
              </div>
              <button type="submit" className="sf-auth-submit" style={{ background: store.primary_color }} disabled={authLoading}>
                {authLoading ? t('Creating...', 'جاري الإنشاء...') : t('Register & Continue', 'تسجيل ومتابعة')}
              </button>
            </form>
          )}

          <button
            onClick={() => setCheckoutMode('form')}
            style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer', marginTop: 12, fontFamily: 'inherit' }}
          >
            {t('← Continue as guest', 'متابعة كزائر →')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-modal-overlay" onClick={onClose}>
      <div className="sf-modal sf-checkout-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sf-modal-close" onClick={onClose}><FiX /></button>
        <h2>{t('Checkout', 'إتمام الطلب')}</h2>
        <form onSubmit={handleSubmit}>
          {customer ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 6, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: store.primary_color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                {customer.name?.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{customer.name}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{customer.email}{customer.phone ? ` · ${customer.phone}` : ''}</div>
              </div>
              <FiCheck size={16} color={store.primary_color} />
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">{t('Full Name', 'الاسم الكامل')} *</label>
                <input className="form-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{t('Phone', 'الهاتف')} *</label>
                  <input className="form-input" dir="ltr" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: toEnDigits(e.target.value) })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('Email', 'البريد')} ({t('optional', 'اختياري')})</label>
                  <input type="email" className="form-input" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
                </div>
              </div>
            </>
          )}

          {/* Only show order type toggle if both options are available */}
          {deliveryAvailable && pickupAvailable ? (
            <div className="form-group">
              <label className="form-label">{t('Order Type', 'نوع الطلب')} *</label>
              <div style={{ display: 'flex', gap: 12 }}>
                <label className={`sf-type-option ${form.type === 'delivery' ? 'active' : ''}`} style={form.type === 'delivery' ? { borderColor: store.primary_color } : {}}>
                  <input type="radio" name="type" value="delivery" checked={form.type === 'delivery'} onChange={(e) => setForm({ ...form, type: e.target.value })} />
                  <FiTruck /> {t('Delivery', 'توصيل')}
                </label>
                <label className={`sf-type-option ${form.type === 'pickup' ? 'active' : ''}`} style={form.type === 'pickup' ? { borderColor: store.primary_color } : {}}>
                  <input type="radio" name="type" value="pickup" checked={form.type === 'pickup'} onChange={(e) => setForm({ ...form, type: e.target.value })} />
                  <FiShoppingBag /> {t('Pickup', 'استلام')}
                </label>
              </div>
            </div>
          ) : null}

          {form.type === 'delivery' && (
            <>
              {selectedArea && (
                <div className="form-group">
                  <label className="form-label">{t('Delivery Area', 'منطقة التوصيل')}</label>
                  <input className="form-input" value={lang === 'ar' ? selectedArea.name : (selectedArea.name_en || selectedArea.name)} disabled />
                </div>
              )}
              {customer && customerAddresses.length > 0 && (
                <div className="form-group">
                  <label className="form-label">{t('Saved Addresses', 'العناوين المحفوظة')}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {customerAddresses.map((addr: any) => (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => setForm({ ...form, addr_block: addr.block || '', addr_street: addr.street || '', addr_house: addr.building || '', addr_floor: addr.floor || '', addr_apartment: addr.apartment || '', addr_notes: addr.notes || '', delivery_address: '' })}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, background: 'white', cursor: 'pointer', textAlign: lang === 'ar' ? 'right' : 'left', fontSize: 12, fontFamily: 'inherit' }}
                      >
                        <FiMapPin size={14} style={{ flexShrink: 0, color: store.primary_color }} />
                        <div style={{ flex: 1 }}>
                          <strong>{addr.label}</strong>{addr.is_default ? ` (${t('Default', 'افتراضي')})` : ''} — {[addr.area, addr.block && `${t('Block', 'ق')} ${addr.block}`, addr.street && `${t('St', 'ش')} ${addr.street}`].filter(Boolean).join(', ')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{t('Block', 'القطعة')} *</label>
                  <input className="form-input" dir="ltr" value={form.addr_block} onChange={(e) => setForm({ ...form, addr_block: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('Street', 'الشارع')} *</label>
                  <input className="form-input" dir="ltr" value={form.addr_street} onChange={(e) => setForm({ ...form, addr_street: e.target.value })} required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{t('House / Building', 'المنزل / المبنى')} *</label>
                  <input className="form-input" dir="ltr" value={form.addr_house} onChange={(e) => setForm({ ...form, addr_house: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('Floor', 'الطابق')} ({t('optional', 'اختياري')})</label>
                  <input className="form-input" dir="ltr" value={form.addr_floor} onChange={(e) => setForm({ ...form, addr_floor: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{t('Apartment', 'الشقة')} ({t('optional', 'اختياري')})</label>
                  <input className="form-input" dir="ltr" value={form.addr_apartment} onChange={(e) => setForm({ ...form, addr_apartment: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('Delivery notes', 'ملاحظات العنوان')} ({t('optional', 'اختياري')})</label>
                  <input className="form-input" value={form.addr_notes} onChange={(e) => setForm({ ...form, addr_notes: e.target.value })} />
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">{t('Notes', 'ملاحظات')} ({t('optional', 'اختياري')})</label>
            <textarea className="form-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          {/* Payment Method */}
          <div className="form-group">
            <label className="form-label">{t('Payment Method', 'طريقة الدفع')} *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Online gateways first */}
              {paymentGateways.map((gw: any) => {
                const label = gw.gateway === 'cbk_knet' || gw.gateway === 'tahseeel_knet' ? t('KNET', 'كي نت')
                  : gw.gateway === 'tahseeel_cc' ? t('Credit Card', 'بطاقة ائتمان')
                  : gw.gateway === 'paypal' ? 'PayPal'
                  : (lang === 'ar' ? gw.name_ar : gw.name);
                return (
                  <label
                    key={gw.gateway}
                    className={`sf-type-option ${form.payment_method === gw.gateway ? 'active' : ''}`}
                    style={form.payment_method === gw.gateway ? { borderColor: store.primary_color } : {}}
                  >
                    <input type="radio" name="payment_method" value={gw.gateway} checked={form.payment_method === gw.gateway} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} />
                    <span style={{ fontSize: 18 }}>💳</span>
                    {label}
                  </label>
                );
              })}

              {/* Cash on delivery - always last */}
              <label
                className={`sf-type-option ${form.payment_method === 'cash' ? 'active' : ''}`}
                style={form.payment_method === 'cash' ? { borderColor: store.primary_color } : {}}
              >
                <input type="radio" name="payment_method" value="cash" checked={form.payment_method === 'cash'} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} />
                <span style={{ fontSize: 18 }}>💵</span>
                {t('Cash on Delivery', 'الدفع عند الاستلام')}
              </label>
            </div>
          </div>

          {/* Promo Code Input */}
          <div className="sf-promo-input-section">
            <label className="form-label"><FiTag /> {t('Promo Code', 'كود الخصم')}</label>
            <div className="sf-promo-input-row">
              <input
                className="form-input"
                value={promoCode}
                onChange={(e) => { const v = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase(); setPromoCode(v); setPromoValid(false); setPromoDiscount(0); setPromoMessage(''); }}
                placeholder={t('Enter promo code', 'أدخل كود الخصم')}
                dir="ltr"
                style={{ fontFamily: 'monospace', letterSpacing: 1, textAlign: 'left' }}
              />
              <button
                type="button"
                className="sf-promo-apply-btn"
                onClick={validatePromo}
                disabled={promoLoading || !promoCode.trim()}
                style={{ background: store.primary_color }}
              >
                {promoLoading ? '...' : t('Apply', 'تطبيق')}
              </button>
            </div>
            {promoMessage && (
              <p className={`sf-promo-message ${promoValid ? 'sf-promo-success' : 'sf-promo-error'}`}>
                {promoMessage}
              </p>
            )}
          </div>

          <div className="sf-checkout-summary">
            <div className="sf-summary-row">
              <span>{t('Subtotal', 'المجموع الفرعي')}</span>
              <span>{store.currency_symbol} {cart.getTotal().toFixed(3)}</span>
            </div>
            {form.type === 'delivery' && (
              <div className="sf-summary-row">
                <span>{t('Delivery Fee', 'رسوم التوصيل')}</span>
                {isFreeDelivery ? (
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>
                    <span style={{ textDecoration: 'line-through', color: 'var(--gray-400)', fontWeight: 400, marginInlineEnd: 6 }}>{store.currency_symbol} {rawDeliveryFee.toFixed(3)}</span>
                    {t('FREE', 'مجاني')}
                  </span>
                ) : (
                  <span>{store.currency_symbol} {deliveryFee.toFixed(3)}</span>
                )}
              </div>
            )}
            {form.type === 'delivery' && freeThreshold > 0 && !isFreeDelivery && (
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: 11, marginBottom: 4, color: 'var(--gray-500)', textAlign: 'center' }}>
                  {t('Add', 'أضف')} {store.currency_symbol} {(freeThreshold - cartSubtotal).toFixed(3)} {t('more for free delivery', 'للحصول على توصيل مجاني')}
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'var(--gray-100)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(100, (cartSubtotal / freeThreshold) * 100)}%`, background: store.primary_color, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            {promoDiscount > 0 && (
              <div className="sf-summary-row sf-summary-discount">
                <span>{t('Discount', 'الخصم')}</span>
                <span>- {store.currency_symbol} {promoDiscount.toFixed(3)}</span>
              </div>
            )}
            <div className="sf-summary-row sf-summary-total">
              <strong>{t('Total', 'المجموع')}</strong>
              <strong>{store.currency_symbol} {total.toFixed(3)}</strong>
            </div>
          </div>

          <button
            type="submit"
            className="sf-add-to-cart-btn"
            disabled={submitting}
            style={{ background: store.primary_color }}
          >
            {submitting ? t('Placing Order...', 'جاري تقديم الطلب...') : t('Place Order', 'تقديم الطلب')}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProductPage({ slug, productSlug, store, lang, isRTL, layout, cart, t, storeBase }: any) {
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [modalOptions, setModalOptions] = useState<Record<string, Record<string, number>>>({});
  const [modalNotes, setModalNotes] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/storefront/${slug}/product/${productSlug}`)
      .then(res => { setProduct(res.data); setLoading(false); })
      .catch(() => { setProduct(null); setLoading(false); });
  }, [slug, productSlug]);

  if (loading) return <div className="sf-loading"><div className="sf-spinner" /></div>;
  if (!product) return (
    <div className="sf-not-found">
      <h1>{t('Product not found', 'المنتج غير موجود')}</h1>
      <Link to={storeBase || '/'} style={{ color: store.primary_color }}>{t('Back to Store', 'العودة للمتجر')}</Link>
    </div>
  );

  const allImages = [product.image, ...(product.additional_images || [])].filter(Boolean);
  const hasDiscount = product.discount_active;
  const effectivePrice = hasDiscount ? product.effective_price : product.price;

  const toggleOption = (group: string, choice: string, type: string, maxSel: number) => {
    setModalOptions(prev => {
      const g = { ...(prev[group] || {}) };
      if (type === 'radio' || type === 'select') {
        return { ...prev, [group]: g[choice] ? {} : { [choice]: 1 } };
      }
      if (g[choice]) { delete g[choice]; } else {
        if (maxSel > 0 && Object.keys(g).length >= maxSel) return prev;
        g[choice] = 1;
      }
      return { ...prev, [group]: g };
    });
  };

  const getOptionsTotal = () => {
    let total = 0;
    (product.option_groups || []).forEach((grp: any) => {
      const sel = modalOptions[grp.name] || {};
      grp.choices?.forEach((c: any) => {
        const cName = lang === 'ar' && c.name_ar ? c.name_ar : c.name;
        if (sel[cName]) total += parseFloat(c.price || '0') * sel[cName];
      });
    });
    return total;
  };

  const addToCart = () => {
    const options: SelectedOption[] = [];
    (product.option_groups || []).forEach((grp: any) => {
      const sel = modalOptions[grp.name] || {};
      grp.choices?.forEach((c: any) => {
        const cName = lang === 'ar' && c.name_ar ? c.name_ar : c.name;
        if (sel[cName]) options.push({ groupName: grp.name, choiceName: cName, price: parseFloat(c.price || '0'), quantity: sel[cName] });
      });
    });
    cart.addItem({
      product_id: product.id, name: lang === 'ar' && product.name_ar ? product.name_ar : product.name,
      price: Number(effectivePrice), image: product.image, options, notes: modalNotes,
    });
    toast.success(t('Added to cart', 'تمت الإضافة'));
  };

  const unitTotal = (Number(effectivePrice) + getOptionsTotal()) * qty;

  return (
    <div
      className={`storefront sf-${layout} ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ '--sf-primary': store.primary_color, '--sf-secondary': store.secondary_color, '--sf-accent': store.accent_color, '--sf-font': store.font_family, fontFamily: store.font_family || 'inherit' } as any}
    >
      <Helmet>
        <html lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} />
        <title>{(lang === 'ar' ? (product.name_ar || product.name) : product.name)} - {(lang === 'ar' ? (store.name_ar || store.name) : store.name)}</title>
        <meta name="description" content={
          lang === 'ar'
            ? (product.meta_description || product.description_ar || product.description || '')
            : (product.meta_description || product.description || '')
        } />
        <link rel="canonical" href={`${window.location.origin}/product/${product.slug}`} />
        <link rel="alternate" hrefLang="ar" href={`${window.location.origin}/product/${product.slug}?lang=ar`} />
        <link rel="alternate" hrefLang="en" href={`${window.location.origin}/product/${product.slug}?lang=en`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${(lang === 'ar' ? (product.name_ar || product.name) : product.name)} - ${(lang === 'ar' ? (store.name_ar || store.name) : store.name)}`} />
        <meta property="og:description" content={lang === 'ar' ? (product.description_ar || product.description || '') : (product.description || '')} />
        <meta property="og:url" content={`${window.location.origin}/product/${product.slug}`} />
        {product.image && <meta property="og:image" content={`${STORAGE_URL}/${product.image}`} />}
        <meta property="product:price:amount" content={product.effective_price || product.price} />
        <meta property="product:price:currency" content={store.currency || 'KWD'} />
        {product.stock > 0 || !product.track_stock ? <meta property="product:availability" content="in stock" /> : <meta property="product:availability" content="out of stock" />}
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: lang === 'ar' ? (product.name_ar || product.name) : product.name,
          description: (lang === 'ar' ? (product.description_ar || product.description) : product.description) || '',
          image: product.image ? `${STORAGE_URL}/${product.image}` : undefined,
          sku: product.sku || undefined,
          url: `${window.location.origin}/product/${product.slug}`,
          brand: { '@type': 'Brand', name: store.name },
          offers: {
            '@type': 'Offer',
            url: `${window.location.origin}/product/${product.slug}`,
            priceCurrency: store.currency || 'KWD',
            price: product.effective_price || product.price,
            availability: (product.stock > 0 || !product.track_stock) ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            seller: { '@type': 'Organization', name: store.name },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: store.name, item: window.location.origin },
            ...(product.category ? [{ '@type': 'ListItem', position: 2, name: lang === 'ar' ? (product.category.name_ar || product.category.name) : product.category.name, item: `${window.location.origin}?category=${product.category.slug}` }] : []),
            { '@type': 'ListItem', position: product.category ? 3 : 2, name: lang === 'ar' ? (product.name_ar || product.name) : product.name },
          ],
        })}</script>
      </Helmet>

      <div className="sf-product-page">
        {/* Breadcrumb */}
        <div className="sf-breadcrumb">
          <Link to={storeBase || '/'}>{(lang === 'ar' && store.name_ar) || store.name}</Link>
          <span> / </span>
          {product.category && <><Link to={`${storeBase || '/'}?category=${product.category.slug}`}>{lang === 'ar' && product.category.name_ar ? product.category.name_ar : product.category.name}</Link><span> / </span></>}
          <span>{lang === 'ar' && product.name_ar ? product.name_ar : product.name}</span>
        </div>

        <div className="sf-product-page-grid">
          {/* Image Gallery */}
          <div className="sf-pp-gallery">
            <div className="sf-pp-main-image">
              {allImages.length > 0 ? (
                <img src={`${STORAGE_URL}/${allImages[selectedImage]}`} alt={product.name} />
              ) : (
                <div className="sf-product-placeholder" style={{ height: 400 }}><FiShoppingBag size={48} /></div>
              )}
              {hasDiscount && (
                <span className="sf-discount-badge" style={{ background: store.primary_color }}>
                  {product.discount_type === 'percentage' ? `${product.discount_value}%` : `${store.currency_symbol} ${Number(product.discount_value).toFixed(3)}`} {t('OFF', 'خصم')}
                </span>
              )}
              {product.is_featured && <span className="sf-featured-badge">{t('Featured', 'مميز')}</span>}
            </div>
            {allImages.length > 1 && (
              <div className="sf-pp-thumbnails">
                {allImages.map((img: string, i: number) => (
                  <div key={i} className={`sf-pp-thumb ${i === selectedImage ? 'active' : ''}`} onClick={() => setSelectedImage(i)}>
                    <img src={`${STORAGE_URL}/${img}`} alt="" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="sf-pp-info">
            {product.category && (
              <span className="sf-pp-category" style={{ color: store.primary_color }}>
                {lang === 'ar' && product.category.name_ar ? product.category.name_ar : product.category.name}
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <h1 className="sf-pp-title" style={{ margin: 0 }}>{lang === 'ar' && product.name_ar ? product.name_ar : product.name}</h1>
              <button
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: (lang === 'ar' && product.name_ar) || product.name, url });
                  } else {
                    navigator.clipboard.writeText(url);
                    toast.success(t('Link copied', 'تم نسخ الرابط'));
                  }
                }}
                style={{
                  flexShrink: 0, padding: '8px 12px', borderRadius: 8,
                  border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 13,
                  color: '#6b7280', marginTop: 4,
                }}
                title={t('Share', 'مشاركة')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                <span>{t('Share', 'مشاركة')}</span>
              </button>
            </div>

            <div className="sf-pp-price">
              <span className="sf-pp-current-price" style={{ color: store.primary_color }}>
                {store.currency_symbol} {Number(effectivePrice).toFixed(3)}
              </span>
              {(hasDiscount || (product.compare_price && product.compare_price > product.price)) && (
                <span className="sf-pp-old-price">{store.currency_symbol} {Number(hasDiscount ? product.price : product.compare_price).toFixed(3)}</span>
              )}
              {hasDiscount && product.discount_end && (
                <span className="sf-pp-sale-timer">
                  {t('Ends', 'ينتهي')}: {new Date(product.discount_end).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>

            {hasDiscount && product.discount_quantity && (
              <div className="sf-pp-limited">
                <span>{t('Limited offer', 'عرض محدود')}: </span>
                <strong>{Math.max(0, product.discount_quantity - (product.discount_used || 0))}</strong> {t('left', 'متبقي')}
              </div>
            )}

            {product.description && (
              <p className="sf-pp-description">
                {lang === 'ar' && product.description_ar ? product.description_ar : product.description}
              </p>
            )}

            {/* Attributes */}
            {product.attributes?.length > 0 && (
              <div className="sf-pp-attributes">
                {product.attributes.map((attr: any, i: number) => (
                  <div key={i} className="sf-pp-attr">
                    <span className="sf-pp-attr-key">{attr.key}</span>
                    <span className="sf-pp-attr-val">{attr.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Option Groups */}
            {product.option_groups?.length > 0 && (
              <div className="sf-pp-options">
                {product.option_groups.map((group: any, gi: number) => (
                  <div key={gi} className="sf-pp-option-group">
                    <h4>
                      {lang === 'ar' && group.name_ar ? group.name_ar : group.name}
                      {group.required && <span style={{ color: '#ef4444', marginInlineStart: 4 }}>*</span>}
                    </h4>
                    <div className="sf-pp-choices">
                      {group.choices?.map((choice: any, ci: number) => {
                        const cName = lang === 'ar' && choice.name_ar ? choice.name_ar : choice.name;
                        const isSelected = !!(modalOptions[group.name] || {})[cName];
                        const cPrice = parseFloat(choice.price || '0');
                        return (
                          <div key={ci} className={`sf-opt-choice ${isSelected ? 'sf-opt-choice-selected' : ''}`} onClick={() => toggleOption(group.name, cName, group.type, group.max_selections || 0)}>
                            <div className="sf-opt-choice-info">
                              <span className="sf-opt-choice-name">{cName}</span>
                              {cPrice > 0 && <span className="sf-opt-choice-price" style={{ color: store.primary_color }}>{store.currency_symbol} {cPrice.toFixed(3)} +</span>}
                            </div>
                            <div className={`sf-opt-indicator ${group.type === 'radio' || group.type === 'select' ? 'sf-opt-radio' : 'sf-opt-check'} ${isSelected ? 'sf-opt-active' : ''}`}>
                              {isSelected && <FiCheck size={12} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <textarea
                  className="sf-opt-notes-input"
                  placeholder={t('Special instructions...', 'ملاحظات خاصة...')}
                  value={modalNotes} onChange={e => setModalNotes(e.target.value)} rows={2}
                />
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="sf-pp-actions">
              <div className="sf-pp-qty">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}><FiMinus /></button>
                <span>{qty}</span>
                <button onClick={() => setQty(q => q + 1)}><FiPlus /></button>
              </div>
              <button
                className="sf-pp-add-btn"
                onClick={() => { for (let i = 0; i < qty; i++) addToCart(); }}
                disabled={!store.is_open}
                style={{ background: store.primary_color }}
              >
                <FiShoppingBag /> {t('Add to Cart', 'أضف إلى السلة')}
              </button>
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="sf-pp-tags">
                {product.tags.map((tag: string, i: number) => (
                  <span key={i} className="sf-pp-tag"><FiTag size={11} /> {tag}</span>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="sf-pp-details">
              {product.sku && <div><span>{t('SKU', 'رقم المنتج')}:</span> {product.sku}</div>}
              {product.calories && <div><span>{t('Calories', 'السعرات')}:</span> {product.calories}</div>}
              {product.preparation_time && <div><span>{t('Prep Time', 'وقت التحضير')}:</span> {product.preparation_time}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
